import { useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ContractStatus,
  DeploymentKind,
  CustomerSegment,
  GasType,
  RobotStatus,
  UserRole,
  // 계약 사실화 1단계 정정9(26-07-29) — 계약 형태 한 단어('판매'/'렌탈'·'SALE'/'RENTAL')
  // → 줄 사유 규칙의 정본. 견적 카드의 '계약 유형'이 이 값을 그대로 찍고 있었다.
  contractTypeToLineReason,
  formatRobotShortId,
  type CustomerSummaryDto,
  type OwnerEntity,
} from '@bhops/shared';
import { customersApi } from '@/api/customers';
import { deploymentsApi } from '@/api/deployments';
import { DeploymentStatusCell } from '@/lib/deployment-status-label';
import { modelsApi } from '@/api/models';
import { robotsApi } from '@/api/robots';
import { RobotStatusPill } from '@/components/StatusPill';
import {
  customerMasterApi,
  CUSTOMER_MASTER_STATUS_LABEL,
  CUSTOMER_MASTER_STATUS_COLOR,
  type CustomerMasterRow,
  type CustomerMasterRobotLine,
} from '@/api/customer-master';
import { infrastructureOrdersApi } from '@/api/infrastructure-orders';
import { extractApiError } from '@/api/client';
import { customerNotesApi } from '@/api/customer-notes';
import { Button, Textarea } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SpareBadge } from '@/components/SpareBadge';
import { useToast } from '@/components/Toast';
import { MapUrlIcon } from '@/components/MapUrlIcon';
import { useAuthStore } from '@/stores/auth';
import { canEditAny, canEditSales, canSeePrice } from '@/permissions';
import { kstDateTime, kstYmd } from '@/lib/format-date';
import { formatDDay } from '@/lib/d-day';
import { formatKrwOrNone } from '@/lib/amount-or-none';
// 계약 사실화 1단계 정정9(26-07-29) — 계약 형태 표기·접기는 정본 한 벌만 쓴다.
import {
  CONTRACT_REASON_EMPTY,
  foldContractReasonToKind,
  foldContractReasonsToKindSummary,
} from '@/lib/contract-reason';
// 개선 9건 차수2e(26-08-04) — 배정·설치 단계 표기와 '배정 전' 판정은 정본 한 벌만 쓴다.
import { formatContractHandoff, isAwaitingRobotAssignment } from '@/lib/contract-handoff';
// 다계약 표시 차수3(26-08-05) — 헤더 D-Day 가 보는 계약(판매 전용이 아닌 계약 기준)과 '왜
// 비었는지'의 문구는 정본 한 벌만 쓴다. 정정2(26-08-06) — 시트도 이제 **같은 자리**를 읽는다
// (사본 없음). 계약 카드의 반납일 표기(판매 전용이면 날짜가 아니라 없는 이유)와 진행 상태
// 배지가 보는 계약 판정, 그 배지가 무엇을 말하는 칸인지의 설명도 그 정본에서 온다.
import {
  endDateDisplay,
  isSaleOnlyByLines,
  pickStatusBasisContract,
  resolveTermBasis,
  termBasisNoReturnExplanation,
  termBasisNoReturnLabel,
  termBasisNoReturnReason,
  STATUS_BASIS_STAGE_TEXT,
  TERM_BASIS_CANDIDATE_NOUN,
} from '@/lib/term-basis';
import { ReturnRobotModal } from '../robots/ReturnRobotModal';
import { SwapRobotModal } from './SwapRobotModal';
import { RowMoreMenu } from './RowMoreMenu';
import { CustomerEditModal } from './CustomerEditModal';
import { CustomerMasterCreateModal } from '../business/CustomerMasterCreateModal';

// S1 Phase 7 (R2 #6) — CustomerDetailPage 종합 정리.
// 5탭(개요/매장/영업/계약/인프라 발주) 구조 폐기 → 단일 페이지.
// 좌측 메인은 2-column grid (우측 320px '고객사 서류' 패널은 제거됨 — 사이드바
// '고객사 서류' 탭으로 이동):
//   좌측 컬럼: 기본 정보(OverviewSection) + 담당자·사양(CustomerMasterSpecSection)
//   우측 컬럼: 계약 요약(ContractSummarySection) + 인프라 발주(InfrastructureSection) + 배치 로봇(DeploymentsSection)
// docs/superpowers/specs/2026-05-28-s1-business-ux-cleanup-design.md §3.1
// docs/superpowers/plans/2026-05-29-s1-business-ux-comprehensive.md Phase 7

const SEGMENT_LABEL: Record<CustomerSegment, string> = {
  UNKNOWN: '미분류',
  FRANCHISE_HQ: '프랜차이즈 본사',
  FRANCHISE_STORE: '프랜차이즈 매장',
  INDEPENDENT_RESTAURANT: '독립 레스토랑',
  HOTEL_RESORT: '호텔 · 리조트',
  CATERING: '케이터링',
  RETAIL_CHAIN: '리테일 체인',
  CAFE: '카페',
  BAKERY: '베이커리',
  CLOUD_KITCHEN: '클라우드 키친',
  EDUCATION: '교육',
  RESEARCH: '연구',
  GOVERNMENT: '공공기관',
  EXPORT_DISTRIBUTOR: '해외 디스트리뷰터',
  INTERNAL_OPS: '사내 운영',
};

const SECTION_PREVIEW_LIMIT = 5;

export function CustomerDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const userRole = useAuthStore((s) => s.user?.role);
  // windows-ux-edit (2026-06, 슬라이드7 / plan §D-7) — 고객사 마스터 write(편집·할당
  // 요청)는 백엔드 @Roles(ADMIN/MANAGER/SALES_MANAGER) 와 일치하도록 canEditSales.
  // (기존엔 SALES_MANAGER 누락 → 영업팀장이 편집/할당요청을 못 했음.)
  const canEdit = userRole ? canEditSales(userRole) : false;
  // 삭제(customersApi.remove)는 customers 컨트롤러의 별도 엔드포인트 — 사업팀
  // write 격리(고객사 마스터) 범위 밖이라 종전대로 ADMIN/MANAGER 로 유지.
  const canDelete = userRole === UserRole.ADMIN || userRole === UserRole.MANAGER;
  // round3 #8d — '기본 정보 편집'(customers PATCH)도 백엔드 CASL(customers.service
  // checkCan + ability.factory)이 SALES_MANAGER 의 Customer update 를 허용한다(과거
  // 주석의 'ADMIN/MANAGER 전용·SALES 403' 은 CASL 부여 전 stale). canEditSales 로 정렬.
  const canEditBasic = canEdit;
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<CustomerSummaryDto | null>(null);
  // round5 #2a — '마스터 편집'을 페이지 이탈(?editId 핸드오프) 없이 이 자리에서
  // 모달로 연다. masterQ 가 이미 동일 CustomerMasterRow 를 들고 있다.
  const [masterEditOpen, setMasterEditOpen] = useState(false);
  // docs/work_log v1210 — 삭제 confirm dialog.
  const [deleteOpen, setDeleteOpen] = useState(false);
  // 개선계획 §7 (결정 ⑩) — 회수 완료 시 고객사가 자동 종료되므로, 오조작을
  // 되돌릴 출구가 필요하다.
  const [unretireOpen, setUnretireOpen] = useState(false);
  const [unplanReturnOpen, setUnplanReturnOpen] = useState(false);
  const canRequest = canEdit;

  const customerQ = useQuery({
    queryKey: ['customers', id],
    queryFn: () => customersApi.get(id),
    enabled: !!id,
  });

  // S1 Phase 7 — 헤더의 상태 배지 / D-Day 를 위해 master row 를 페이지
  // 레벨에서 fetch. CustomerMasterSpecSection / ContractSummarySection 의
  // 동일 쿼리 키와 staleTime 으로 React Query 가 dedupe.
  const masterQ = useQuery({
    queryKey: ['customer-master', 'one', id],
    queryFn: () => customerMasterApi.get(id),
    staleTime: 30_000,
    enabled: !!id,
  });

  // round7 R2 — 삭제 전 의존성 요약(활성 계약/배치·배정 로봇/미종결 SO 경고).
  const depsQ = useQuery({
    queryKey: ['customers', 'deps', id],
    queryFn: () => customersApi.dependencySummary(id),
    enabled: !!id && deleteOpen,
    staleTime: 10_000,
  });

  const deleteMx = useMutation({
    mutationFn: () => customersApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customer-master'] });
      toast.success('고객사를 삭제했습니다.');
      setDeleteOpen(false);
      navigate('/business/master', { replace: true });
    },
    onError: (err) => {
      toast.error(extractApiError(err), '삭제 실패');
    },
  });

  // 종료 취소 — 자동 종료 직전 상태인 '회수 예정'으로 되돌린다. 보존된 회수
  // 사유를 그대로 다시 넘겨 사유 표시가 비지 않게 한다.
  // ⚠ 정정5(26-08-06) — 이 경로는 **사람이 찍은 종료**(statusPinned)에서만 열린다. 그 경로만이
  //   회수 사유를 보존하고(로봇 회수 완료 헬퍼), 서버가 'return_planned' 에 사유를 요구한다.
  //   버튼 조건에 그 근거를 적어 두었다.
  const unretireMx = useMutation({
    mutationFn: () =>
      customerMasterApi.updateStatus(id, 'return_planned', masterQ.data?.returnReason ?? undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-master'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['active-customers'] });
      toast.success('고객사 종료를 취소했습니다. 상태가 회수 예정으로 돌아갔습니다.');
      setUnretireOpen(false);
    },
    onError: (err) => {
      toast.error(extractApiError(err), '종료 취소 실패');
    },
  });

  // 2026-10-22 오너 — "이거 되돌리는게 안되는데"(상세의 '회수 예정'). 고객사 마스터 표의
  // '자동 계산으로 되돌리기'와 같다 — 고정을 풀면 회수 사유도 비워지고 현장 앱 회수 탭에서 빠진다.
  // 회수 예정일은 남는다(캘린더 일정을 지우면 날짜와 함께 이 상태도 풀린다).
  const unplanReturnMx = useMutation({
    mutationFn: () => customerMasterApi.updateStatus(id, null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-master'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['active-customers'] });
      toast.success("'회수 예정'을 취소했습니다. 상태가 자동 계산으로 돌아갔습니다.");
      setUnplanReturnOpen(false);
    },
    onError: (err) => {
      toast.error(extractApiError(err), '회수 예정 취소 실패');
    },
  });

  if (customerQ.isLoading) {
    return <div style={loadingBlock}>불러오는 중…</div>;
  }
  if (customerQ.error || !customerQ.data) {
    return (
      <div style={errBlock}>
        <span style={{ color: 'var(--fg-danger)' }}>
          {customerQ.error ? extractApiError(customerQ.error) : '고객사를 찾을 수 없습니다'}
        </span>
      </div>
    );
  }

  const c = customerQ.data;
  const masterRow = masterQ.data;

  // ── 개선 9건 차수2e(26-08-04) ㉡ — 헤더의 '진행 상태 배지 + D-Day' 를 한 계약으로 ──
  //
  // 종전엔 한 줄이 **두 계약을 말했다**: 배지는 서버가 가장 임박한 만기 계약으로 합성하고
  // (customer-master.row synthesizeStatus), D-Day 는 masterRow.contract(= 가장 최근 등록
  // 계약)의 잔여일을 읽었다. 슭돈 이대역본점처럼 계약이 2건이면 "설치 완료 · D-1460" 처럼
  // 배지는 구독 계약, D-Day 는 판매 계약을 가리켰다.
  // 기준 선택은 화면의 일이므로(서버는 row.contract 의 날짜 칸에 다른 계약 값을 섞지 않는다)
  // 여기서 soonestContract 를 **직접** 읽어 배지와 같은 계약으로 맞춘다. 시트도 같은 규칙이다.
  // 계약이 1건이면 soonestContract 가 곧 최신 계약이라 종전과 값이 같다.
  //
  // 🔴 다계약 표시 차수3(26-08-05) ㉠ — 그때 남긴 폴백(`soonestContract ?? contract`)이 이번
  //   차수에 **거짓말이 됐다.** soonestContract 의 뜻이 '먼저 끝나는 **구독** 계약'으로 좁아져
  //   판매만 있는 고객사에서 null 이 되었는데, 폴백이 그 null 을 최신(판매) 계약으로 메워
  //   시트가 '–' + '판매 — 반납 없음' 이라고 말하는 고객사의 헤더에 'D-1094' 를 찍었다.
  //   판정과 문구는 정본 한 벌(lib/term-basis)에서 온다 — 폴백은 **서버가 판정 자체를
  //   내리지 않는 구버전**에서만 살아 있다(그 파일 머리 주석에 갈림길의 근거).
  const headerContractCount = masterRow?.contracts?.length ?? 0;
  const headerTermBasis = masterRow ? resolveTermBasis(masterRow) : null;
  const headerDDayContract = headerTermBasis?.facts ?? null;
  /**
   * D-Day 가 **판매 때문에** 비었는가 — 비었으면 그 이유(배지 글자·제목이 이유별로 갈린다).
   * 정정3(26-08-06) — 종전엔 이 자리가 `reason === 'sale-only'` 리터럴 비교였다. 서버가
   * 'live-sale-only'(구독 해지 + 판매 생존)를 내리기 시작하면 그 비교가 **조용히 거짓**이 되어
   * 배지가 아예 사라진다 — D-Day 자리가 이유 없이 빈 채로 남는다. 갈림길을 정본에 맡긴다.
   */
  const headerNoReturnReason = headerTermBasis
    ? termBasisNoReturnReason(headerTermBasis.reason)
    : null;

  // ── 다계약 표시 차수3 정정2(26-08-06) ㉡ — 헤더 한 줄이 **두 계약을 섞어 말하던** 자리 ──
  //
  // 🔴 무엇이 틀렸었나 — 이 줄에는 값이 둘이다: 진행 상태 배지(masterRow.status)와 D-Day.
  //   서버가 그 둘의 기준을 **독립으로** 판정하게 된 뒤(pickStatusBasisContract) 두 값이 다른
  //   계약을 볼 수 있게 됐는데, 줄 끝의 '{계약번호} 기준' 라벨은 D-Day 쪽 계약만 적었다.
  //   실측 모양(옛 구독 해지 + 오늘의 판매 살아 있음): 배지는 살아 있는 판매 계약의 단계
  //   ('설치 완료'), D-Day 는 해지된 그 구독 계약의 잔여일, 라벨은 해지된 계약번호 —
  //   한 줄이 '설치 완료 · D-148 · CNT-…(해지된 구독) 기준' 이 되어 배지까지 그 계약의 것으로
  //   읽혔다.
  //
  // 어떻게 닫는가 — **두 값이 같은 계약을 볼 때만** 라벨을 한 번 적는다(종전 글자 그대로).
  // 갈리면 값마다 그 값의 기준을 옆에 적는다. 배지의 기준을 알 수 없으면(그 칸을 내리지 않는
  // 응답) 라벨이 배지까지 아는 척하지 않도록 D-Day 쪽으로 범위를 좁혀 적는다.
  // 계약이 1건이면 라벨이 아예 붙지 않는다 — 종전 헤더와 한 픽셀도 같다.
  const headerStatusContract = pickStatusBasisContract(masterRow?.contracts);
  /** 배지와 D-Day 가 **같은 계약**을 보는가(둘 다 알 때만 참). */
  const headerBasisShared =
    !!headerStatusContract &&
    !!headerDDayContract &&
    headerStatusContract.id === headerDDayContract.id;
  /**
   * 배지 옆에 **그 값의 기준**을 따로 적어야 하는가.
   *
   * 🔴 다계약 표시 차수3 정정3(26-08-06) ㉢ — 종전 조건에 `!!headerDDayContract` 가 있어서
   *   **D-Day 가 없으면 라벨이 아예 붙지 않았다.** 판매 계약만 여럿인 고객사(계약 2건 이상 ·
   *   네 칸이 '–' 라 D-Day 없음 · basis = 'sale-only')에서는 그래서 진행 상태 배지가 **어느
   *   판매 계약**의 단계인지 헤더가 침묵했다 — 같은 고객사의 시트는 '계약 N건' 배지로 그
   *   계약을 밝히므로 상세가 덜 말하는 쪽이었다(값이 여럿인 계약 중 하나를 가리키면서 어느
   *   계약인지 말하지 않는 것은 이번 차수가 닫은 결함과 같은 성질이다).
   *
   * 갈림길은 D-Day 의 **유무가 아니라** 아래 D-Day 라벨이 이미 그 계약을 말했는가다
   * (`headerBasisShared` — 그때만 라벨 하나로 족하다. 두 벌이면 중복 소음이다).
   * 배지 자체가 없을 때(status 없음) 붙이지 않는 것은 아래 렌더 조건이 그대로 지킨다 —
   * 설명할 값이 없는 라벨은 무엇의 기준인지 말할 수 없다.
   */
  const headerStatusBasisNeedsLabel = !!headerStatusContract && !headerBasisShared;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div>
        <Link to="/business/master" className="kuun-caption" style={crumbStyle}>
          ← 고객사 마스터
        </Link>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            marginTop: 6,
          }}
        >
          <div style={{ flex: 1 }}>
            <h1 className="kuun-h2" style={{ margin: 0 }}>
              {c.displayName}
            </h1>
            <div className="kuun-caption" style={{ marginTop: 4, color: 'var(--fg-muted)' }}>
              {c.legalName} · {SEGMENT_LABEL[c.segment as CustomerSegment] ?? c.segment}
              {c.salesOwnerCode && ` · 영업 ${c.salesOwnerCode}`}
              {!c.active && ' · 비활성'}
            </div>
            {/* S1 Phase 7 — 진행 상태 배지 + D-Day. master row 가 로드된
                뒤에만 노출. spec §3.1 의 헤더 우상단 표시 위치. */}
            {masterRow && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                {masterRow.status && (
                  <span
                    style={{
                      ...statusBadgeBase,
                      color: CUSTOMER_MASTER_STATUS_COLOR[masterRow.status].fg,
                      background: CUSTOMER_MASTER_STATUS_COLOR[masterRow.status].bg,
                      border: `1px solid ${CUSTOMER_MASTER_STATUS_COLOR[masterRow.status].border}`,
                    }}
                  >
                    {CUSTOMER_MASTER_STATUS_LABEL[masterRow.status]}
                  </span>
                )}
                {/* ㉡ — D-Day 라벨이 이 배지의 계약까지 말해 주지 않을 때. 라벨을 배지 **바로
                    옆**에 두는 것이 요점이다: 줄 끝에 하나만 있으면 그 라벨이 두 값 전부를
                    가리키는 것처럼 읽힌다(그것이 ㉡ 이 닫은 결함이다).
                    정정3(26-08-06) ㉢ — D-Day 가 **없어도** 배지가 있으면 배지의 기준을 밝힌다
                    (판매 계약만 여럿인 고객사에서 헤더가 침묵하던 자리 —
                    headerStatusBasisNeedsLabel 주석에 그 모양). */}
                {/* 정정5(26-08-06) — 아래 title 이 두 곳에서 틀렸었다: ⓐ '배정·설치 단계' —
                    이 칸에는 이번 차수부터 '종료'가 온다(계약이 끝났고 회수할 것이 없다는 값이지
                    배정·설치의 한 단계가 아니다). ⓑ '지켜볼 계약(구독)' — 서버의 후보 규칙은
                    '구독'이 아니라 '판매 전용이 아닌 것'이라 줄 0개(형태 미상) 계약도 후보다.
                    두 말 모두 **정본 한 벌**에서 가져온다 — 시트의 같은 설명이 같은 글자를 쓴다
                    (종전엔 두 화면이 각자 리터럴을 들고 있어 한쪽만 고쳐질 자리였다). */}
                {headerContractCount > 1 &&
                  headerStatusBasisNeedsLabel &&
                  headerStatusContract &&
                  // 배지가 없으면 이 라벨이 설명할 값도 없다(붙이면 무엇의 기준인지 알 수 없다).
                  masterRow.status && (
                    <span
                      className="kuun-caption"
                      style={basisLabelStyle}
                      data-testid="cd-header-status-basis"
                      title={
                        `진행 상태는 이 계약의 ${STATUS_BASIS_STAGE_TEXT}를 말합니다. ` +
                        `D-Day는 반납일을 지켜볼 ${TERM_BASIS_CANDIDATE_NOUN}을 따르므로 ` +
                        '다른 계약일 수 있습니다.'
                      }
                    >
                      진행 상태 {headerStatusContract.humanId} 기준
                    </span>
                  )}
                {headerDDayContract &&
                  headerDDayContract.daysRemaining !== null &&
                  headerDDayContract.daysRemaining !== undefined && (
                    <span style={dDayStyle} data-testid="cd-header-dday">
                      {formatDDay(headerDDayContract.daysRemaining)}
                    </span>
                  )}
                {/* 다계약 표시 차수3(26-08-05) ㉠ — 기준 계약이 **없을 때** 왜 D-Day 가
                    없는지 말한다. '판매라서'와 '계약이 없어서'는 같은 빈 자리의 **다른
                    이유**이므로 같은 말을 하지 않는다:
                      • 판매뿐 → 이 배지(시트와 같은 글자). 계약은 살아 있고 반납이라는
                        사건만 없다는 사실을 말하지 않으면 운영자는 계약이 누락된 줄 안다.
                      • 정정3(26-08-06) 살아 있는 것만 판매(구독은 해지) → 같은 자리에 **다른
                        글자**가 온다. "계약은 판매뿐"이 이 고객사에서 거짓이기 때문이고, 끝난
                        구독의 반납일이 아래 계약 카드에 그대로 있다는 사실도 제목이 말한다
                        (회수가 남아 있을 수 있다 — 그 날짜를 화면에서 지우지 않았다).
                      • 계약이 하나도 없음 → 이 자리는 종전에도 비어 있었고, 그 고객사는
                        아래 계약 섹션 자체가 견적 카드로 바뀌어 있어(또는 없어) 빈 D-Day 가
                        '누락'으로 읽힐 여지가 없다. 시트도 그 경우엔 배지를 붙이지 않는다.
                    ✅ 정정2(26-08-06) — 아래 계약 카드의 '반납일' 칸도 이제 같은 말을 한다.
                      종전 이 자리의 ⚠ 는 "카드는 여전히 그 계약의 endDate 를 그린다(후속)"
                      였고, 그 후속을 닫았다 — 판정은 서버가 내린 줄 원문 + shared 정본이라
                      화면이 새로 판정하지 않는다(lib/term-basis isSaleOnlyByLines). */}
                {headerNoReturnReason && (
                  <span
                    className="kuun-caption"
                    style={dDayNoneStyle}
                    data-testid="cd-header-dday-none"
                    title={termBasisNoReturnExplanation(headerNoReturnReason, 'D-Day')}
                  >
                    {termBasisNoReturnLabel(headerNoReturnReason)}
                  </span>
                )}
                {/* 계약이 여럿이면 이 D-Day 가 **어느 계약 것인지** 화면이 말한다.
                    계약이 1건이면 말할 것이 없으므로 종전과 같은 한 줄이다.
                    정정2(26-08-06) ㉡ — 배지와 같은 계약을 볼 때는 종전 글자 그대로 한 번만
                    적는다(중복 소음 금지). 갈릴 때만 이 라벨이 자기 값의 이름을 붙여 어느 값의
                    기준인지 밝힌다. */}
                {headerContractCount > 1 && headerDDayContract && (
                  <span
                    className="kuun-caption"
                    style={basisLabelStyle}
                    data-testid="cd-header-dday-basis"
                  >
                    {headerBasisShared ? '' : 'D-Day '}
                    {headerDDayContract.humanId} 기준
                  </span>
                )}
              </div>
            )}
            {/* FB §4b — '회수 예정' 사유 표시. status 가 return_planned 이고
                사유가 있을 때만 노출. */}
            {masterRow?.status === 'return_planned' && masterRow.returnReason && (
              <div
                className="kuun-caption"
                style={{ marginTop: 6, color: 'var(--fg-danger)' }}
                data-testid="return-reason"
              >
                회수 사유: {masterRow.returnReason}
              </div>
            )}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8 }}>
              {canEditBasic && (
                <Button type="button" variant="secondary" onClick={() => setEditing(c)}>
                  기본 정보 편집
                </Button>
              )}
              {/* Phase 5 — 마스터 통합 편집 (계약 / 로봇까지).
                  round5 #2a — 페이지 이탈 없이 이 자리에서 모달 직접 오픈.
                  저장 시 cascade invalidation 이 ['customer-master'] prefix 를
                  무효화해 본 페이지 쿼리도 자동 갱신된다. */}
              <Button
                type="button"
                variant="secondary"
                disabled={!masterQ.data}
                onClick={() => setMasterEditOpen(true)}
                title="고객사 마스터 통합 편집"
              >
                마스터 편집
              </Button>
              {/* round5 #18(역제안) — '이 고객사를 누가 뭘 바꿨나' 직행.
                  감사 로그는 ADMIN 전용 라우트라 동일 게이트 적용. */}
              {userRole === UserRole.ADMIN && (
                <Link
                  to={`/audit-logs?action=customer-master.*&resourceId=${id}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    height: 32,
                    borderRadius: 'var(--r-btn)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--fg-muted)',
                    textDecoration: 'none',
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                  title="이 고객사 마스터의 변경 이력"
                >
                  변경 이력
                </Link>
              )}
              {/* 🔴 다계약 표시 차수3 정정5(26-08-06) — 조건에 `statusPinned` 가 붙었다.
                  이번 차수에 '종료'가 **두 뿌리**를 갖게 됐다:
                    · 사람이 찍은 종료 — 로봇 회수 완료가 `manualStatusOverride:'retired'` 를
                      남긴다(deployments/robot-return.helper). `statusPinned = true`.
                    · 파생 종료   — 기준 계약이 판매 전용인데 끝났다(EXPIRED/TERMINATED)는
                      **계산 결과**다(서버 synthesizeStatus). 아무도 종료시킨 적이 없어
                      `manualStatusOverride = null` → `statusPinned = false`.
                  뒤쪽에 이 버튼을 띄우면 **되돌릴 종료가 없다**. 실제로 누르면 서버가
                  `status='return_planned'` 에 회수 사유를 요구하는데(customer-master.service),
                  파생 종료 고객사의 `returnReason` 은 언제나 null 이고 이 화면에 사유를 넣을
                  입구가 없어 **항상 400** 인 막다른 길이었다. 통과했다면 더 나빴다 — 이번 차수가
                  지운 그 거짓 '회수 예정'이 파생 계산으로는 못 고치는 자리에 박히고 현장팀에
                  회수 알림까지 나간다.
                  '취소'라는 개념은 **사람이 찍은 값에만** 있다. 파생 '종료'를 바꾸려면 계약을
                  고쳐야 한다(그 화면은 마스터 편집이다). */}
              {masterRow?.status === 'retired' && masterRow.statusPinned && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setUnretireOpen(true)}
                  data-testid="customer-unretire-button"
                >
                  종료 취소
                </Button>
              )}
              {masterRow?.status === 'return_planned' && masterRow.statusPinned && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setUnplanReturnOpen(true)}
                  data-testid="customer-unplan-return-button"
                >
                  회수 예정 취소
                </Button>
              )}
              {canDelete && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => setDeleteOpen(true)}
                  data-testid="customer-delete-button"
                >
                  삭제
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* S1 Phase 7 — 단일 column 2-col main. 우측 '고객사 서류' 패널은
          사이드바 탭으로 이동(제거). spec §3.1 wireframe 참조. */}
      <div style={mainColumn}>
        <div style={twoColInner}>
          <div style={leftCol}>
            <OverviewSection customer={c} masterRow={masterRow} />
            <CustomerMasterSpecSection customerId={id} />
            {/* round10 요구1 — 좌하단 CRM 접촉 메모 타임라인. */}
            <CrmTimelineSection customerId={id} />
          </div>
          <div style={rightCol}>
            <ContractSummarySection customerId={id} canRequest={canRequest} />
            <InfrastructureSection customerId={id} canRequest={canRequest} />
            <RobotInfoSection customerId={id} />
          </div>
        </div>
      </div>

      {editing && (
        <CustomerEditModal
          open={Boolean(editing)}
          onClose={() => setEditing(null)}
          customer={editing}
        />
      )}

      {/* round5 #2a — 마스터 통합 편집 모달 (페이지 이탈 없이). */}
      {masterEditOpen && masterQ.data && (
        <CustomerMasterCreateModal
          open
          editing={masterQ.data}
          onClose={() => setMasterEditOpen(false)}
        />
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="고객사 삭제"
        message={
          `"${c.displayName}" 고객사를 삭제하시겠습니까?\n\n` +
          // round7 R2 — 의존성 경고(차단은 안 함): 활성 계약·로봇이 연결돼
          // 있어도 조용히 지워지던 문제의 가시화.
          (depsQ.data &&
          (depsQ.data.activeContracts > 0 ||
            depsQ.data.deployedRobots > 0 ||
            depsQ.data.reservedRobots > 0 ||
            depsQ.data.openServiceOrders > 0)
            ? `주의: 연결된 데이터가 있습니다. 활성 계약 ${depsQ.data.activeContracts}건, ` +
              `배치 로봇 ${depsQ.data.deployedRobots}대, 예약 배정된 로봇 ${depsQ.data.reservedRobots}대, ` +
              `끝나지 않은 서비스 오더 ${depsQ.data.openServiceOrders}건입니다.\n` +
              `삭제해도 이 데이터는 남습니다. 모두 정리하려면 삭제한 뒤 고객사 마스터에서 [완전 삭제]를 눌러 주세요.\n\n`
            : '') +
          `삭제하면 목록에서 숨겨지고, 나중에 복구할 수 있습니다.`
        }
        confirmLabel="삭제"
        danger
        busy={deleteMx.isPending}
        onConfirm={() => deleteMx.mutate()}
        onCancel={() => {
          if (deleteMx.isPending) return;
          setDeleteOpen(false);
        }}
      />

      <ConfirmDialog
        open={unretireOpen}
        title="고객사 종료 취소"
        message={
          `"${c.displayName}"의 종료를 취소하고 상태를 '회수 예정'으로 되돌립니다.\n\n` +
          `로봇 상태와 배치 이력은 복구되지 않습니다. 실제로 운영 중이라면 로봇을 다시 배치 등록해야 합니다.`
        }
        confirmLabel="종료 취소"
        busy={unretireMx.isPending}
        onConfirm={() => unretireMx.mutate()}
        onCancel={() => {
          if (unretireMx.isPending) return;
          setUnretireOpen(false);
        }}
      />

      <ConfirmDialog
        open={unplanReturnOpen}
        title="회수 예정 취소"
        message={
          `"${c.displayName}"의 '회수 예정'을 풀고, 상태를 로봇 배치를 기준으로 한 자동 계산으로 되돌립니다.\n\n` +
          `회수 사유가 지워지고 현장 앱 '로봇 회수' 탭에서 빠집니다. 회수 예정일이 잡혀 있으면 날짜는 남습니다. ` +
          `캘린더에서 회수 일정을 지우면 날짜도 비워집니다.`
        }
        confirmLabel="회수 예정 취소"
        busy={unplanReturnMx.isPending}
        onConfirm={() => unplanReturnMx.mutate()}
        onCancel={() => {
          if (unplanReturnMx.isPending) return;
          setUnplanReturnOpen(false);
        }}
      />
    </div>
  );
}

function SectionHeader({
  title,
  count,
  moreHref,
  showMore,
  moreLabel,
}: {
  title: string;
  count?: number;
  moreHref?: string;
  showMore?: boolean;
  // 개선 9건 차수2e(26-08-04) ㉣ — 링크 글자를 부르는 쪽이 정할 수 있게 열었다.
  // 한 화면에 '더 보기' 링크가 둘이면(인프라 발주 + 계약) 어디로 가는 링크인지 글자만
  // 보고는 알 수 없다 — 스크린리더는 같은 이름의 링크 두 개로 읽는다. 기본값은 종전 글자.
  moreLabel?: string;
}) {
  return (
    <div style={sectionHeader}>
      <div className="kuun-label">
        {title}
        {typeof count === 'number' && (
          <span style={{ color: 'var(--fg-muted)', marginLeft: 6, fontWeight: 400 }}>
            ({count})
          </span>
        )}
      </div>
      {showMore && moreHref && (
        <Link to={moreHref} style={moreLinkStyle}>
          {moreLabel ?? '더 보기 →'}
        </Link>
      )}
    </div>
  );
}

function OverviewSection({
  customer,
  masterRow,
}: {
  customer: CustomerSummaryDto;
  masterRow?: CustomerMasterRow;
}) {
  // S1 Phase 9 (R2 #7) — 위치/지도 URL 은 master row 에서만 노출.
  // CustomerSummaryDto 에는 주소 필드가 없어 master row(get) 값을 사용.
  const address = masterRow?.address || masterRow?.region || null;
  const mapUrl = masterRow?.addressMapUrl ?? null;
  return (
    <section>
      <SectionHeader title="개요" />
      <div style={card}>
        <div style={kvGrid}>
          <KV k="상호 (브랜드명)" v={customer.displayName} />
          <KV k="법인명" v={customer.legalName} />
          <KV
            k="고객 유형"
            v={SEGMENT_LABEL[customer.segment as CustomerSegment] ?? customer.segment}
          />
          <KV k="영업 코드" v={customer.salesOwnerCode ?? '–'} mono />
          <KV k="국가" v={customer.countryCode} mono />
          <KV k="법인" v={customer.ownerEntity} />
          {/* round3 #3 — '매장 수'는 사용자 입력값(expectedStoreCount)을 표시한다.
              customer.storeCount(실제 Store 관계 수)는 신규 고객사에서 항상 0 이라
              입력한 값(1)이 0 으로 보이던 버그. 입력값은 masterRow 에 있다. */}
          {/* round11 (요구 10) — 쇼잉 여부(전시/시연 매장). 미입력이면 '–'.
              값은 마스터 편집 모달에서 입력한다(masterRow). */}
          <KV
            k="쇼잉 여부"
            v={masterRow?.isShowing == null ? '–' : masterRow.isShowing ? '예' : '아니오'}
          />
          <KV
            k="매장 수"
            v={masterRow?.expectedStoreCount != null ? String(masterRow.expectedStoreCount) : '–'}
            mono
          />
          <KV
            k="위치"
            v={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>{address ?? '–'}</span>
                <MapUrlIcon url={mapUrl} />
              </span>
            }
          />
          <KV
            k="상태"
            v={
              customer.active ? (
                <span style={{ color: 'var(--robot-operating)' }}>활성</span>
              ) : (
                <span style={{ color: 'var(--fg-muted)' }}>비활성</span>
              )
            }
          />
          {/* 추가수정 2차(26-07-27) §2 — 브라우저 로캘 대신 KST 고정. 상세
              화면이라 시각까지 + 기준 라벨(결정 ②A). */}
          <KV k="등록일" v={kstDateTime(customer.createdAt, { label: true }) ?? '–'} />
        </div>
        {/* 추가수정(26-08-28) — 고객사 마스터의 '비고'(Customer.notes)가 이 화면에
            없어서, 마스터에 적은 메모("간택기 배송 9월 1일 협의 완료" 류)를 상세에서
            확인할 수 없었다. 값은 이미 masterRow 에 실려 온다 — 화면이 안 그렸을 뿐.
            줄바꿈이 있는 긴 메모라 KV 격자 칸에 넣지 않고 전폭 한 줄로 둔다
            (pre-wrap: 마스터에서 여러 줄로 적었으면 여기서도 여러 줄로 보인다).
            비어 있으면 줄 자체를 그리지 않는다 — '–' 한 줄은 정보가 아니라 소음이다.
            아래 'CRM 메모'(다건 접촉 로그)와는 별개다: 비고 = 고객사 자체의 단일
            메모(마스터에서 편집), CRM 메모 = 통화·방문 기록의 누적. */}
        {masterRow?.notes ? (
          <div style={kvNotesRow} data-testid="customer-notes-row">
            <span style={kvKey}>비고</span>
            <span style={{ ...kvVal, whiteSpace: 'pre-wrap' }}>{masterRow.notes}</span>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function KV({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div style={kvRow}>
      <span style={kvKey}>{k}</span>
      <span style={{ ...kvVal, fontFamily: mono ? 'var(--font-mono)' : undefined }}>{v}</span>
    </div>
  );
}

/**
 * 계약 사실화 1단계 정정9(26-07-29) — 계약 카드의 '계약 형태' 한 줄.
 *
 * 왜 필요했나 — 계약 형태를 말하는 화면이 셋(로봇 목록 '구분' · 계약 상세 줄 표 ·
 * 고객사 목록 '계약 형태')인데 **이 화면만 말하지 않았다**. 그래서 운영자가 이 카드에서
 * 판매 계약인지 알려면 "구독료 칸이 비고 판매가 칸에 금액이 있으니 판매겠지"로 금액에서
 * 거꾸로 짐작해야 했다 — 금액이 아직 안 들어간 계약에서는 짐작조차 안 된다.
 *
 * 표기는 오너 지시(26-07-29) 그대로 **판매/구독 두 단어만**. 파일럿·시연·행사·사내·
 * 금융리스는 '구독'으로 접는다(접기 정본 = lib/contract-reason).
 * ⚠ 접기는 표시 전용이다 — 저장은 7값 원문이고 감사 로그도 접지 않는다.
 *
 * 접기의 입력은 줄의 원문(row.robots[].reason)이고 **대수**만큼 세므로 혼합 계약은
 * '판매 1 · 구독 2' 가 된다 — 고객사 목록의 같은 칸과 같은 문구를 낸다(두 화면이 같은
 * 계약을 두고 다른 말을 하지 않게).
 *
 * ⚠ 아래 세 줄(대수 펼치기 + 모르는 값이면 접지 않기)은 고객사 목록
 * (pages/business/CustomerMasterPage formatContractTypeCell)에도 있다. 규칙 자체는 정본
 * 한 벌이고 이것은 호출 규약이다 — 정본이 줄 배열({reason, quantity}[])을 직접 받게 되면
 * 두 곳이 한 줄로 줄어든다(그 파일은 이번 차수에 수정 금지라 호출부에 둔다).
 *
 * 개선 9건 차수2e(26-08-04) ㉠ — 인자를 **한 계약의 줄 + 그 계약의 서버 요약**으로 좁혔다
 * (종전: row 전체). 계약이 2건인 고객사에서 row.robots(= 최신 계약의 줄)를 읽으면 모든
 * 카드가 같은 형태를 말한다 — 구독 계약 카드에 '판매'가 찍힌다.
 */
function contractKindLine(
  robots: ReadonlyArray<CustomerMasterRobotLine>,
  serverSummary: string | null,
): string {
  const reasons = robots.flatMap((rb) =>
    Array.from({ length: Math.max(0, rb.quantity) }, () => rb.reason),
  );
  if (reasons.length > 0 && reasons.every((r) => foldContractReasonToKind(r) !== null)) {
    return foldContractReasonsToKindSummary(reasons) ?? CONTRACT_REASON_EMPTY;
  }
  // 줄이 없거나 모르는 값이 섞였으면 서버 요약 원문 그대로(절반 번역·조용한 뭉개기 금지).
  return serverSummary || CONTRACT_REASON_EMPTY;
}

/**
 * 개선 9건 차수2e(26-08-04) ㉠ — 계약 카드 **한 장**이 그리는 값.
 *
 * 왜 서버 요약을 그대로 쓰지 않고 이 모양을 두는가 — 카드는 두 갈래 입력에서 나온다:
 *   ⓐ 서버 응답의 `row.contracts[]` 항목(정상). 그 계약의 값만 들어 있다.
 *   ⓑ 계약별 칸을 싣지 않는 **옛 서버 응답**에서는 `row.contract` 한 벌(종전 동작).
 * 두 갈래를 카드 컴포넌트 안에서 갈라 보면 카드가 "어느 칸이 어느 계약 것인지"를 매번
 * 다시 판단하게 된다. 그 판단을 여기 한 번만 하고, 카드는 받은 값을 그대로 그린다.
 */
interface ContractCardData {
  id: string;
  humanId: string;
  /** 계약 상태 원문(ACTIVE / DRAFT / …). 종전 카드가 원문을 찍었으므로 그대로 둔다. */
  status: string;
  /**
   * 배정·설치 단계. **null = 이 응답이 말해 주지 않았다**(옛 서버) — 카드는 단계를 말하지
   * 않고 '할당 요청' 판정도 종전 규칙으로 되돌린다. 짐작해서 채우지 않는다.
   */
  handoffStatus: string | null;
  /** 이 계약 줄들의 형태 요약(서버 원문). 화면 문구는 contractKindLine 이 만든다. */
  contractTypeSummary: string | null;
  contractedAt: string | null;
  installCompletedAt: string | null;
  endDate: string | null;
  daysRemaining: number | null;
  depositKrw: number | null;
  subsidyKrw: number | null;
  referralFeeKrw: number | null;
  totalSubscriptionFeeKrw: number | null;
  totalSalesPriceKrw: number | null;
  totalContractQty: number;
  robots: CustomerMasterRobotLine[];
  /** 편집·저장이 닿는 계약인가(마스터 편집 모달이 이 계약만 고친다). */
  isPrimary: boolean;
  /** 반납일이 가장 임박한 계약인가(헤더 D-Day·진행 상태의 기준). */
  isSoonest: boolean;
  /** 이 계약에 예약(배정)된 로봇 대수. null = 알 수 없음(옛 서버). */
  reservedRobotCount: number | null;
}

/**
 * 개선 9건 차수2e(26-08-04) ㉠ — 응답 한 줄 → 계약 카드 목록.
 *
 * 순서는 서버가 준 순서(반납일 임박 순)를 그대로 쓴다 — 운영자가 계약 목록을 볼 이유는
 * "다음에 뭐가 오는가"이고, 헤더 D-Day 가 보는 계약이 맨 위에 오게 된다.
 *
 * ⚠ 카드의 모든 칸은 **그 계약 항목의 값**만 쓴다. row.contract 를 섞지 않는다 —
 *   그 객체의 금액 5칸은 **전 계약 합**이고(차수2e ②) 날짜는 최신 계약 것이라, 카드에
 *   쓰면 오너가 본 그 결함(판매 계약 카드에 구독 계약 로봇의 배치일 2024.12.01)이 돌아온다.
 */
function buildContractCards(row: CustomerMasterRow): ContractCardData[] {
  const summaries = row.contracts ?? [];
  // 차수2e ④ 이전 서버(차수2c)는 contracts[] 를 **뼈대만** 실었다(금액·줄·도입일 없음).
  // 그 응답으로 계약별 카드를 그리면 빈 칸을 다른 계약 값으로 채우게 되므로, 카드는
  // 종전 한 장(row.contract) 경로로 되돌린다. 감춰지는 계약이 있으면 섹션이 말한다.
  const carriesCardFields = summaries.length > 0 && summaries.every((s) => Array.isArray(s.robots));
  if (carriesCardFields) {
    return summaries.map((s) => ({
      id: s.id,
      humanId: s.humanId,
      status: s.status,
      handoffStatus: s.handoffStatus ?? null,
      contractTypeSummary: s.contractType,
      contractedAt: s.contractedAt,
      installCompletedAt: s.installCompletedAt,
      endDate: s.endDate,
      daysRemaining: s.daysRemaining,
      depositKrw: s.depositKrw,
      subsidyKrw: s.subsidyKrw,
      referralFeeKrw: s.referralFeeKrw,
      totalSubscriptionFeeKrw: s.totalSubscriptionFeeKrw,
      totalSalesPriceKrw: s.totalSalesPriceKrw,
      totalContractQty: s.totalContractQty,
      robots: s.robots ?? [],
      isPrimary: s.isPrimary,
      isSoonest: s.isSoonest,
      reservedRobotCount: s.reservedRobotCount ?? null,
    }));
  }
  const c = row.contract;
  if (!c) return [];
  return [
    {
      id: c.id,
      humanId: c.humanId,
      status: c.status,
      // 옛 서버 응답에는 이 계약의 배정 단계가 없다 → 말하지 않는다.
      handoffStatus: null,
      contractTypeSummary: c.contractType,
      contractedAt: c.contractedAt,
      installCompletedAt: c.installCompletedAt,
      endDate: c.endDate,
      daysRemaining: c.daysRemaining,
      depositKrw: c.depositKrw,
      subsidyKrw: c.subsidyKrw,
      referralFeeKrw: c.referralFeeKrw,
      totalSubscriptionFeeKrw: c.totalSubscriptionFeeKrw,
      totalSalesPriceKrw: c.totalSalesPriceKrw,
      totalContractQty: c.totalContractQty,
      robots: row.robots ?? [],
      isPrimary: true,
      isSoonest: true,
      reservedRobotCount: null,
    },
  ];
}

/**
 * 감사 ㉡(26-08-04) — '할당 요청' 한 번이 **말할 수 있는 것**.
 *
 * humanId 는 "이 버튼이 붙은 카드의 계약"이라는 화면 안의 사실이다. 그것이 곧 제조팀
 * 알림에 실릴 계약이라는 뜻은 **아니다**(요청 API 가 고객사만 받는다). 두 뜻을 한 칸에
 * 담지 않기 위해, 계약번호를 단정해도 되는지를 별도 칸으로 같이 넘긴다.
 */
interface AssignRequestTarget {
  /** 버튼이 붙은 카드의 계약번호. */
  humanId: string;
  /**
   * 이 계약번호를 '대상 계약'이라고 말해도 사실인가 — **응답이 계약 1건이라고 말해 준**
   * 경우에만 참이다. 그때는 서버가 고를 수 있는 계약도 그 하나뿐이다.
   * 계약 목록을 싣지 않는 옛 응답은 '1건'이 아니라 **모른다**이므로 거짓이 된다.
   */
  canNameTargetContract: boolean;
}

/**
 * 감사 ㉠(26-08-04) — 이 계약 카드에 '할당 요청'(제조팀 재촉) 버튼을 띄울까.
 *
 * 🔴 무엇이 틀렸었나 — 판정이 **배정 단계와 부족분만** 봤고 그 계약이 아직 살아 있는지를
 *   보지 않았다. 계약 해지는 계약 상태만 해지로 바꾸고 배정 단계는 손대지 않으므로
 *   (contracts.service.terminate — 리포 어디에도 배정 단계를 '취소'로 쓰는 코드가 없다),
 *   배정 전에 해지된 계약은 **영구히 '배정 대기'로 남는다**. 그래서 해지된 계약 카드에
 *   버튼이 계속 떴고, 누르면 제조팀에 알림이 실제로 나갔다. 종전 판정에 있던 거친
 *   살아있음 확인(고객사 진행 상태)이 판정을 계약별로 쪼개는 과정에서 빠진 자리다.
 *
 * '살아 있다' = **그 계약이 계약 중(ACTIVE)** 이다. 초안(DRAFT)은 넣지 않는다. 근거는
 * 재촉을 **받는 쪽**에 있다:
 *   · 제조팀 배정 인박스(assignments.service.list)는 ACTIVE 계약만 싣는다.
 *   · 로봇↔계약 자동 연결(contract-handoff.helper)도 ACTIVE 만 대상이고, 주석이
 *     초안은 대상이 아니라고 못 박아 뒀다.
 *   → 초안 계약을 재촉하면 재촉받은 팀의 목록에 그 계약이 아예 없다(할 수 없는 부탁).
 * 고객사 마스터의 '계약 중·설치 준비·설치 완료' 세 상태가 "최신 계약 ACTIVE"와 같은
 * 뜻이라고 서버가 이미 적어 뒀으므로(customer-master.row ASSIGNABLE_CUSTOMER_STATUSES),
 * ACTIVE-only 는 종전 거친 판정이 뜻하던 것을 계약 단위로 정밀화한 것이지 새 규칙이 아니다.
 * (고객사 삭제가 해지로 넘길 때 쓰는 '초안·계약 중' 두 값은 "아직 끝나지 않았는가"라는
 *  **다른 질문**이라 여기 가져오지 않는다.)
 *
 * ⚠ 살아있음 판정의 정본을 서버 담당이 같은 시간에 만들고 있다. 이 작업트리에는 아직
 *   없어서 불러올 수 없으므로 **어휘만** 맞춘다 — 값은 공용 열거(@bhops/shared
 *   ContractStatus)에서 가져오고 자체 상태 목록을 만들지 않는다. 그 정본이 들어오면
 *   아래 한 줄을 그것으로 바꾼다(두 벌을 만들지 않기 위해 lib 에 새 파일을 두지 않았다).
 */
function showAssignRequestForCard(card: ContractCardData, legacyFallback: boolean): boolean {
  // 죽은 계약(해지·만료·초안)에는 **어느 경로에서도** 버튼을 띄우지 않는다.
  if (card.status !== ContractStatus.ACTIVE) return false;
  // 계약별 단계를 말해 주지 않는 옛 응답에서는 종전 판정으로 되돌린다(짐작 금지).
  if (card.handoffStatus === null || card.reservedRobotCount === null) return legacyFallback;
  return (
    isAwaitingRobotAssignment(card.handoffStatus) && card.reservedRobotCount < card.totalContractQty
  );
}

// S1 Phase 7 — 계약 요약 카드. embedded ContractsPage 대신 thin summary.
// ContractsPage 자체는 /contracts 라우트에서 그대로 사용.
//
// 개선 9건 차수2e(26-08-04) ㉠ — 이 섹션이 **계약별 카드**를 그린다(종전: 최신 1건).
// 종전엔 row.contract 한 벌만 그렸고, 추가 판매를 새 계약으로 유도한 뒤로는 그 한 벌이
// '가장 최근에 등록한 계약'이라 오너가 상세를 열면 **판매 계약만** 보였다. 게다가 그 카드의
// 금액·로봇 줄·도입일이 전부 최신 계약 기준이라, 옛 구독 계약의 로봇 배치일(2024.12.01)이
// 새 판매 계약 카드의 도입일로 찍혔다. 계약이 1건인 고객사(대다수)에서는 카드 한 장이
// 종전과 같은 모양으로 나온다 — 제목·간격·칸 순서 모두 그대로다.
function ContractSummarySection({
  customerId,
  canRequest,
}: {
  customerId: string;
  canRequest: boolean;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['customer-master', 'one', customerId],
    queryFn: () => customerMasterApi.get(customerId),
    staleTime: 30_000,
  });
  // 추가수정(26-08-14) §1 — 계약 줄의 '구분 사양'을 그 모델의 축 이름과 함께 보여주기 위한
  // 모델 레지스트리(GEN2·헤드리스 '방향' · Zephyr '타입' · GEN1 계열 '로더 통신 케이블').
  // 표기는 같은 화면 '로봇 정보' 표가 이미 쓰는 정본(formatSpec — "방향: Right")을 그대로
  // 재사용한다 — 한 화면에서 같은 것을 두 가지로 부르지 않는다.
  // queryKey 는 다른 화면들과 같은 ['models'] 라 react-query 가 디듀프한다(마스터 편집
  // 모달이 이 화면에 있어 대개 이미 캐시에 있다). 아직 안 왔으면 축 이름 없이 값만 나온다.
  const modelsQ = useQuery({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    staleTime: 5 * 60_000,
  });
  const variantAxisLabelByModelCode = new Map<string, string | null>(
    (modelsQ.data?.items ?? []).map((m) => [m.code, m.variantAxisLabel ?? null]),
  );

  // S1 Phase 7 (R2 #6) — '할당 요청' 버튼. 제조팀에 ROBOT_ASSIGNMENT_REQUESTED
  // 알람 재발송.
  //
  // 개선 9건 차수2e(26-08-04) ㉤ — 버튼은 이제 **계약 카드마다** 뜨고 인자로 그 계약을
  // 받는다. 다만 그것은 **화면 안의 사실**일 뿐이다:
  //   ⚠ 요청 API 는 고객사만 받는다(POST /customer-master/:id/request-robot-assignment).
  //     서버가 알림에 실을 계약을 스스로 고른다(customer-master.service
  //     requestRobotAssignment — status asc·createdAt desc 첫 계약). 계약을 지정해 보낼
  //     방법이 지금은 없으므로 무리하게 바꾸지 않고, 계약이 여럿일 때 그 사실을 카드가
  //     말한다(지어내지 않는다).
  //   ⚠ 서버 쓰로틀도 **고객사 단위 60초**다. 계약이 2건이면 두 번째 카드의 요청이
  //     'throttled' 로 돌아온다 — 종전 화면은 그때도 "보냈습니다"를 띄웠다(조용한 유실).
  //     응답을 보고 말을 바꾼다.
  //
  // 🔴 감사 ㉡(26-08-04) — 성공 토스트가 위 사실을 **뒤집고 있었다.** 같은 파일의 카드
  //   안내문은 "알림에 실릴 계약번호는 서버가 고릅니다"라고 정직하게 적어 놓았는데,
  //   확정 문구인 토스트가 카드의 계약번호를 '대상 계약'이라고 단정했다. 화면이 말한 계약과
  //   제조팀이 받는 계약이 다를 수 있으므로 그것은 지어낸 말이다. 아래는 **아는 것만**
  //   말한다 — 계약이 1건뿐임을 응답이 말해 준 경우에만 서버의 선택이 그 계약으로 확정되고,
  //   그때만 계약번호를 말한다.
  const requestAssignment = useMutation({
    mutationFn: (_target: AssignRequestTarget) =>
      customerMasterApi.requestRobotAssignment(customerId),
    onSuccess: (res, target) => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      if (res?.status === 'throttled') {
        // 아무것도 나가지 않았으므로 '대상 계약'이라 부를 것이 없다 — 화면이 아는 사실은
        // "이 버튼을 눌렀다"뿐이다.
        toast.warn(
          `방금 보낸 요청이 있어 이번 요청은 보내지 않았습니다. 같은 고객사에는 1분에 한 번만 요청할 수 있습니다. ` +
            `잠시 뒤 다시 시도해 주세요. 요청하려던 계약은 ${target.humanId}입니다.`,
          '할당 요청 보류',
        );
        return;
      }
      toast.success(
        target.canNameTargetContract
          ? `제조팀에 할당 요청을 보냈습니다. 대상 계약은 ${target.humanId}입니다.`
          : `제조팀에 할당 요청을 보냈습니다. 알림에 실릴 계약번호는 서버가 고르므로 ` +
              `${target.humanId} 계약이 아닐 수 있습니다.`,
      );
    },
    onError: (err) => {
      toast.error(extractApiError(err), '할당 요청 실패');
    },
  });

  // 가격 가시성 — 계약 금액(구독료/판매가/보증금)은 가격 권한 역할만(서버
  // PriceMaskInterceptor 가 2차 방어로 *Krw 도 null 마스킹). 목록 시트와 동일 규칙.
  const role = useAuthStore((s) => s.user?.role);
  const priceVisible = role ? canSeePrice(role) : false;
  // 계약 사실화 1단계 정정8(26-07-29) — 금액 표기는 정본 한 벌(lib/amount-or-none)만
  // 쓴다. 종전 이 지역 함수는 **0 을 '₩0' 으로 찍었다**: 판매 계약의 월 구독료 칸은
  // NOT NULL 이라 '없음'이 0 으로 저장되므로, 같은 계약을 계약 상세는 "월 구독료 없음"
  // 이라 말하고 이 화면은 "구독료 ₩0" 이라 말했다(보증금 0·일시금 0 도 같다).
  // ⚠ 마스킹(priceVisible)이 **앞**, 0 접기가 뒤 — 아래 호출부는 모두
  // `priceVisible ? fmtKrw(...) : '비공개'` 모양이라 0 여부가 새로 새지 않는다.
  const fmtKrw = (n: number | null | undefined): string => formatKrwOrNone(n);

  const row = q.data;
  // FB §4c (결정 B②) — Contract 가 없으면 Deal/마스터 입력값(견적 기준)으로
  // 카드를 채운다. Contract 도 Deal 견적도 없으면 섹션 자체를 숨김.
  if (!row?.contract) {
    if (row?.dealQuote) return <DealQuoteCard quote={row.dealQuote} />;
    return null;
  }
  const cards = buildContractCards(row);
  if (cards.length === 0) return null;
  const multi = cards.length > 1;
  // 서버가 계약 N건을 알렸는데 카드로 그릴 값이 그만큼 안 실려 온 경우(옛 서버 응답).
  // 숫자만 보여 주고 침묵하지 않는다 — 몇 건이 카드에서 빠졌는지 말한다.
  const undrawnCount = Math.max(0, (row.contracts?.length ?? 0) - cards.length);
  // 종전 판정(고객사 진행 상태 + 고객사 전체 배치 대수) — 계약별 단계를 모르는 옛 서버
  // 응답에서만 쓴다. 짐작으로 계약별 판정을 만들어 내지 않는다.
  const legacyShowAssignRequest =
    row.status === 'contracted' &&
    (row.deployedRobotCount ?? 0) < (row.contract.totalContractQty ?? 0);
  // 감사 ㉡ — 요청 토스트가 계약번호를 단정해도 되는지. 응답이 **계약 목록을 실었고 그것이
  // 1건**일 때만 참이다. 목록을 싣지 않은 옛 응답은 "1건"이 아니라 "모른다"이므로 단정하지
  // 않는다(카드가 한 장인 것은 응답이 그만큼만 실어 준 결과일 수도 있다 — 바로 위에서 센
  // '카드에서 빠진 계약'이 그 경우다).
  const canNameTargetContract = (row.contracts?.length ?? null) === 1;

  return (
    <section>
      {/* 계약이 2건 이상이면 제목이 건수를 말한다(1건이면 종전 그대로 '계약').
          ㉣ — 계약 목록으로 가는 길. 인프라 발주 섹션과 같은 moreHref 패턴이고, 한 화면에
          '더 보기' 링크가 둘이 되지 않도록 글자만 이 섹션의 목적지로 적는다. */}
      <SectionHeader
        title="계약"
        count={multi ? cards.length : undefined}
        moreHref={`/business/contracts?customerId=${customerId}`}
        showMore
        moreLabel="계약 목록 →"
      />
      <div style={contractCardStack}>
        {cards.map((cardData) => (
          <ContractCard
            key={cardData.id}
            data={cardData}
            multi={multi}
            canNameTargetContract={canNameTargetContract}
            priceVisible={priceVisible}
            fmtKrw={fmtKrw}
            variantAxisLabelByModelCode={variantAxisLabelByModelCode}
            // ㉤ — 판정을 **그 계약 단위**로. 권한(canRequest) 게이팅은 유지한다.
            // 감사 ㉠ — 살아있음 확인은 판정 정본(showAssignRequestForCard) 안에 있다.
            showAssignRequest={
              canRequest && showAssignRequestForCard(cardData, legacyShowAssignRequest)
            }
            requesting={requestAssignment.isPending}
            onRequestAssignment={() =>
              requestAssignment.mutate({ humanId: cardData.humanId, canNameTargetContract })
            }
          />
        ))}
      </div>
      {undrawnCount > 0 && (
        <div
          className="kuun-caption"
          style={{ marginTop: 8, color: 'var(--fg-warning)' }}
          data-testid="cd-contract-undrawn"
        >
          이 고객사에는 계약이 {row.contracts?.length}건 있지만 여기에는 {cards.length}건만 보여
          드립니다. 나머지 {undrawnCount}건은 계약 목록에서 확인해 주세요.
        </div>
      )}
    </section>
  );
}

/**
 * 개선 9건 차수2e(26-08-04) ㉠ — 계약 카드 한 장.
 *
 * 받은 값만 그린다 — 어느 계약의 값인지 고르는 일은 buildContractCards 가 이미 했다.
 * `multi`(계약 2건 이상)일 때만 카드 머리가 붙는다. 계약이 1건인 고객사에서는
 * 종전과 같은 카드 한 장이 나와야 하기 때문이다(제목·간격·칸 순서 그대로).
 */
function ContractCard({
  data,
  multi,
  canNameTargetContract,
  priceVisible,
  fmtKrw,
  showAssignRequest,
  requesting,
  onRequestAssignment,
  variantAxisLabelByModelCode,
}: {
  data: ContractCardData;
  multi: boolean;
  /**
   * 감사 ㉡ — 요청 안내문이 붙는 조건. 토스트가 계약번호를 단정할 수 있는 그 조건의
   * **반대**다(두 문구가 한 근거를 보게 한다 — 갈리면 안내와 결과가 다른 말을 한다).
   */
  canNameTargetContract: boolean;
  priceVisible: boolean;
  fmtKrw: (n: number | null | undefined) => string;
  showAssignRequest: boolean;
  requesting: boolean;
  onRequestAssignment: () => void;
  /**
   * 추가수정(26-08-14) §1 — 모델 코드 → 그 모델의 '구분 사양' 축 이름. 모델 레지스트리가
   * 아직 안 왔으면 비어 있고, 그때 칸은 축 이름 없이 값만 보여준다(짐작해 붙이지 않는다).
   */
  variantAxisLabelByModelCode?: Map<string, string | null>;
}) {
  const c = data;
  const robots = c.robots;
  const handoffLabel = formatContractHandoff(c.handoffStatus);
  // 다계약 표시 차수3 정정2(26-08-06) ㉠ — 반납일 자리에 무엇을 적는가. 판매 전용 계약이면
  // 날짜가 아니라 **없는 이유**가 온다(표기·판정 모두 lib/term-basis 정본 한 벌 — 시트의
  // '계약 N건' 배지가 같은 계약을 두고 부르는 그 함수다). 종전엔 이 카드가 그 계약의 endDate 를
  // 그대로 찍어서, 시트가 '반납일 없음 (판매 — …)' 이라고 말하는 계약을 상세는
  // '반납일 2029.08.03 D-1094' 라고 말했다.
  const endDate = endDateDisplay(c, isSaleOnlyByLines(robots));
  return (
    <div style={card} data-testid={`cd-contract-card-${c.humanId}`}>
      {/* 계약이 여럿일 때만 — 이 카드가 **어느 계약**이고, 편집·저장이 닿는 계약인지,
          헤더 D-Day 의 기준인지, 배정이 어디까지 갔는지를 카드가 스스로 말한다.
          isPrimary 를 밝히지 않으면 다른 카드를 고치려고 마스터 편집을 열어 **엉뚱한
          계약**이 바뀐다(모달은 최신 계약 한 건만 저장한다). */}
      {multi && (
        <div style={contractCardHead} data-testid={`cd-contract-head-${c.humanId}`}>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{c.humanId}</span>
          <span style={{ color: 'var(--fg-muted)' }}>
            {contractKindLine(robots, c.contractTypeSummary)}
          </span>
          {handoffLabel && <span style={contractHeadTag}>{handoffLabel}</span>}
          {c.isSoonest && (
            <span
              style={contractHeadTag}
              title="반납일이 가장 가까운 계약입니다. 헤더의 D-Day가 이 계약을 기준으로 합니다."
            >
              반납 임박
            </span>
          )}
          {c.isPrimary && (
            <span
              style={contractHeadTagStrong}
              title="'마스터 편집' 저장이 이 계약에 들어갑니다. 다른 계약은 계약 상세에서 고치세요."
            >
              마스터 편집 대상
            </span>
          )}
        </div>
      )}
      <div style={kvGrid}>
        {/* windows-ux-edit Round 2 Phase C (#1) — 계약 번호를 인프라 발주 표에서
              계약 카드로 이동. */}
        <KV k="계약 번호" v={c.humanId} mono />
        {/* 계약 사실화 1단계 정정9(26-07-29) — 계약 형태. 이 화면만 판매 여부를 말하지
              않아 금액이 어느 칸에 찼는지로 짐작해야 했다(contractKindLine 주석).
              고객사 목록의 '계약 형태' 칸과 같은 문구 — 판매/구독 두 단어. */}
        <KV
          k="계약 형태"
          v={
            <span data-testid="cd-contract-kind">
              {contractKindLine(robots, c.contractTypeSummary)}
            </span>
          }
        />
        {/* 추가수정 2차(26-07-27) §2 — 아래 날짜들은 KST 로. 계약일·반납일은
              한국 자정으로 저장돼 UTC 로 자르면 하루 이르게 보였다. */}
        <KV k="계약일" v={kstYmd(c.contractedAt) ?? '–'} />
        {/* round11 (요구 2) — 실제 '도입일'(설치 완료)을 계약일↔반납일 사이에 노출.
              고객사 마스터 목록의 '계약일 → 도입일 → 반납일' 순서와 같은 이름·같은
              자리. 줄 수를 늘리지 않도록 D-Day 는 반납일 줄에 합쳤다(반납일에서
              파생되는 값이라 같은 줄이 오히려 읽힌다). */}
        <KV k="도입일" v={kstYmd(c.installCompletedAt) ?? '–'} />
        {/* 다계약 표시 차수3 정정2(26-08-06) ㉠ — 판매 전용 계약은 이 칸에 **날짜를 적지
              않는다.** 판매는 소유가 고객사로 넘어가 반납이라는 사건이 없는데, DB 에는 endDate
              가 들어 있어서 종전엔 그 날짜와 D-Day 가 그대로 찍혔다. 운영 위험이 구체적이다:
              운영자가 그 D-Day 를 회수 임박으로 읽고 회수 일정을 잡는데 그 로봇은 이미 고객사
              소유라 회수할 것이 없다. 빈칸으로 두면 '미입력'으로 읽히므로 **없는 이유**를 적는다.
              바로 위 계약일·도입일은 그대로 적는다 — 판매도 계약과 설치는 실제로 일어난 사실이고,
              없는 사건은 반납일·D-Day 뿐이다(시트가 같은 판단을 했다 — termFactsText 주석). */}
        <KV
          k="반납일"
          v={
            endDate.saleOnly ? (
              <span style={{ color: 'var(--fg-muted)' }} data-testid="cd-contract-end-date-none">
                {endDate.text}
              </span>
            ) : (
              <>
                {endDate.date}
                {endDate.dday !== null && (
                  <span style={{ marginLeft: 8, color: 'var(--fg-muted)' }}>{endDate.dday}</span>
                )}
              </>
            )
          }
        />
        {/* 사용자 요청 — per-unit '구독료' KV 제거(모델별 구독료/판매가는 아래 표).
              보증금은 가격 권한 역할만 노출. */}
        <KV k="보증금" v={priceVisible ? fmtKrw(c.depositKrw) : '비공개'} />
        {/* 개선 9건 차수2(26-08-04) §7 (결정 ④A) — 보증금 옆에 지원금·소개비.
              보증금과 **같은 규칙**을 그대로 탄다: 마스킹(priceVisible)이 앞, 0·빈 값
              접기(fmtKrw → lib/amount-or-none)가 뒤. 고객에게 주는 계약서 PDF 에는
              인쇄하지 않는다(결정 ⑤A — 내부 지출 정보). */}
        <KV k="지원금" v={priceVisible ? fmtKrw(c.subsidyKrw) : '비공개'} />
        <KV k="소개비" v={priceVisible ? fmtKrw(c.referralFeeKrw) : '비공개'} />
        <KV k="상태" v={c.status} />
      </div>

      {/* 사용자 요청 — 모델별 구독료/판매가 내역 + 총계. 목록 시트는 총계만,
            상세는 모델별 내역으로 분해해 보여준다. 가격 셀은 priceVisible 게이팅. */}
      {robots.length > 0 && (
        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <div style={modelFeeCaption}>모델별 계약 내역</div>
          <table style={modelFeeTable}>
            <thead>
              <tr>
                <th style={mfThL}>모델</th>
                {/* 추가수정(26-08-14) §1 (오너 요구) — 등록 모달에서 모델 줄마다 고르는
                    '구분 사양'(GEN2 Left/Right · Zephyr 기본/갈비 · GEN1 485/TTL).
                    이 값은 이미 응답에 실려 오는데 표가 안 보여줬다 — 그래서 같은 모델을
                    사양만 다르게 두 줄로 계약한 고객사에서 **똑같이 생긴 두 줄**이 됐다
                    (어느 줄이 왼손용인지 이 화면에서 알 수 없었다). 칸 이름과 표기는
                    같은 화면 '로봇 정보' 표와 같다(formatSpec). */}
                <th style={mfThL}>구분 사양</th>
                <th style={mfThR}>수량</th>
                <th style={mfThR}>구독료(대당)</th>
                <th style={mfThR}>구독료 소계</th>
                <th style={mfThR}>판매가(대당)</th>
                <th style={mfThR}>판매가 소계</th>
              </tr>
            </thead>
            <tbody>
              {robots.map((rb, i) => {
                const subLine =
                  rb.unitPriceKrw !== null && rb.unitPriceKrw !== undefined
                    ? rb.unitPriceKrw * rb.quantity
                    : null;
                const saleLine =
                  rb.listPriceKrw !== null && rb.listPriceKrw !== undefined
                    ? rb.listPriceKrw * rb.quantity
                    : null;
                return (
                  <tr key={`${rb.modelCode}-${i}`}>
                    <td style={mfTdL}>{rb.modelDisplay || rb.modelCode}</td>
                    {/* 값의 출처는 등록·편집 모달의 모델별 picklist(ContractLine.desiredDirection).
                        축이 없는 모델(센서리스)이나 미입력이면 formatSpec 이 '–' 를 낸다. */}
                    <td style={mfTdL} data-testid="cd-contract-line-spec">
                      {formatSpec(
                        rb.desiredDirection,
                        variantAxisLabelByModelCode?.get(rb.modelCode),
                      )}
                    </td>
                    <td style={mfTdR}>{rb.quantity}</td>
                    <td style={mfTdR}>{priceVisible ? fmtKrw(rb.unitPriceKrw) : '비공개'}</td>
                    <td style={mfTdR}>{priceVisible ? fmtKrw(subLine) : '비공개'}</td>
                    <td style={mfTdR}>{priceVisible ? fmtKrw(rb.listPriceKrw) : '비공개'}</td>
                    <td style={mfTdR}>{priceVisible ? fmtKrw(saleLine) : '비공개'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td style={mfTdFootL}>합계</td>
                {/* 사양은 더할 수 있는 값이 아니다 — 이 줄의 다른 '합계 없음' 칸과
                    같은 표기('–')로 비운다. */}
                <td style={mfTdFootL}>–</td>
                <td style={mfTdFootR}>{c.totalContractQty ?? '–'}</td>
                <td style={mfTdFootR}>–</td>
                <td style={mfTdFootR}>
                  {priceVisible ? fmtKrw(c.totalSubscriptionFeeKrw) : '비공개'}
                </td>
                <td style={mfTdFootR}>–</td>
                <td style={mfTdFootR}>{priceVisible ? fmtKrw(c.totalSalesPriceKrw) : '비공개'}</td>
              </tr>
            </tfoot>
          </table>
          {/* 계약 사실화 1단계 정정9(26-07-29) — 합계가 어떤 줄을 뺐는지 표가 스스로 밝힌다.
                '총 판매가'(서버 파생)가 이 정정으로 **판매 줄만** 합하게 됐다. 위 '판매가
                소계' 칸은 줄마다 그 줄의 값을 그대로 보여 주므로(구독 줄에 입력된 참고 정가
                = 출주도료 포함), 구독 줄에 정가가 들어 있는 계약에서는 **칸을 더한 값과
                합계가 다르다**. 운영자가 손으로 더해 보고 어긋난 것을 발견하는 것보다
                합계가 스스로 밝히는 쪽이 낫다(계약 상세가 이미 쓰는 관례 — 합계에서 빠진
                줄이 있으면 캡션이 그것을 말한다).
                ⚠ 가격 권한이 없으면 이 문장도 띄우지 않는다 — "이 계약에 0 이 아닌 참고
                정가가 있다"는 사실 자체가 금액 정보이므로(이 화면들의 규칙: 마스킹이 앞). */}
          {priceVisible &&
            robots.some(
              (rb) => rb.reason !== 'SALE' && !!rb.listPriceKrw && rb.listPriceKrw !== 0,
            ) && (
              <div style={{ ...modelFeeCaption, marginTop: 6, fontWeight: 400 }}>
                판매가 소계 합계는 <strong>판매 줄만</strong> 더합니다. 구독 줄에 적힌 판매가는 참고
                정가(출주도료)라 이 계약의 판매 대금이 아닙니다.
              </div>
            )}
        </div>
      )}
      {showAssignRequest && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* 요청이 **어느 계약**에 대한 것인지 말하고, 그 요청이 서버에서 계약을 지정하지
                못한다는 사실도 같이 말한다(요청 API 가 고객사만 받는다 —
                ContractSummarySection 의 ⚠ 주석). 숫자만 보여 주고 침묵하지 않는다.
                계약이 1건뿐임을 응답이 말해 준 고객사에서는 서버의 선택이 그 한 건으로
                확정되므로 붙이지 않는다(종전과 같은 카드 한 장). */}
          {!canNameTargetContract && (
            <div
              className="kuun-caption"
              style={{ color: 'var(--fg-muted)' }}
              data-testid={`cd-assign-note-${c.humanId}`}
            >
              {c.humanId} 계약의 로봇 배정을 재촉합니다. 알림은 고객사 단위로 나가므로 제조팀 알림에
              실릴 계약번호는 서버가 고릅니다.
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="secondary"
              onClick={onRequestAssignment}
              disabled={requesting}
              // 버튼 글자는 종전 그대로 두고(계약 1건이면 화면이 종전과 같아야 한다)
              // 어느 계약인지는 접근성 이름으로도 말한다.
              aria-label={multi ? `${c.humanId} 계약 할당 요청` : undefined}
              title={`계약 ${c.humanId} 의 로봇 배정을 제조팀에 요청합니다`}
            >
              할당 요청
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 계약 사실화 1단계 정정9(26-07-29) — 견적 카드의 '계약 유형'을 운영 표기로.
 *
 * 🔴 종전에는 딜의 값(Deal.expectedContractType)을 **번역 없이** 그대로 찍었다. 딜 등록
 * 모달(pages/deals/DealCreateModal)의 선택값이 화면에는 '렌탈/판매'로 보이지만 서버로
 * 보내는 값은 **영문**('RENTAL' / 'SALE')이라, 계약 전 고객사의 이 카드에 "계약 유형:
 * SALE" 이 그대로 떴다 — 이 차수가 없앤 '영문 코드 노출'의 마지막 잔여였다.
 *
 * 이 값은 DeploymentReason(7값)이 **아니라** 계약 형태 한 단어이고, 한 필드에 두 어휘가
 * 실제로 섞여 저장돼 있다(딜 모달은 영문, 고객사 마스터 경로는 한글 '판매'/'렌탈').
 * 그래서 어휘를 줄 사유로 옮기는 정본(shared contractTypeToLineReason)으로 값을 얻고,
 * 그 사유를 표시 규칙 정본으로 접는다(lib/contract-reason.foldContractReasonToKind).
 * 두 정본을 잇는 것뿐이라 이 화면이 자체 라벨 세트를 갖지 않는다.
 *
 * 인식하지 못하는 값(옵션 관리자가 새로 만든 단어 등)은 **원문 그대로** 둔다 — 두 정본이
 * 이미 그 규약이다(틀린 한국어로 뭉개는 것보다 낫다).
 */
function formatExpectedContractKind(raw: string | null | undefined): string {
  if (!raw) return '–';
  const reason = contractTypeToLineReason(raw);
  return (reason ? foldContractReasonToKind(reason) : null) ?? raw;
}

// FB §4c (결정 B②) — Contract 가 아직 없을 때 '계약' 카드 대신 Deal/마스터
// 입력값(견적 기준)을 보여준다. Contract 행을 만들지 않고(B① 거부), 모달에서
// 입력한 모델/수량/희망 도입일을 그대로 노출해 빈 카드를 방지한다.
function DealQuoteCard({ quote }: { quote: NonNullable<CustomerMasterRow['dealQuote']> }) {
  const modelSummary =
    quote.models.length > 0
      ? quote.models.map((m) => `${m.modelDisplay} × ${m.quantity}`).join(', ')
      : quote.expectedRobotCount != null
        ? `${quote.expectedRobotCount}대, 모델 미정`
        : '–';
  const totalQty = quote.totalQuantity || quote.expectedRobotCount || 0;
  return (
    <section>
      <SectionHeader title="계약" />
      <div style={card}>
        {/* 계약 행이 없음을 명확히 — 견적 단계 안내 배지. */}
        <div
          className="kuun-caption"
          style={{
            display: 'inline-block',
            marginBottom: 10,
            padding: '2px 8px',
            borderRadius: 'var(--r-pill)',
            background: 'var(--bg-sunken)',
            color: 'var(--fg-muted)',
            border: '1px solid var(--border-default)',
          }}
        >
          계약 전 견적 기준
        </div>
        <div style={kvGrid}>
          <KV k="도입 모델" v={modelSummary} />
          <KV k="도입 대수" v={totalQty > 0 ? `${totalQty}대` : '–'} />
          {/* 정정9(26-07-29) — 종전엔 딜의 영문 값('SALE')이 그대로 떴다
              (formatExpectedContractKind 주석). 운영 표기는 판매/구독 두 단어. */}
          <KV
            k="계약 유형"
            v={
              <span data-testid="cd-quote-contract-kind">
                {formatExpectedContractKind(quote.expectedContractType)}
              </span>
            }
          />
          <KV
            k="약정 기간"
            v={
              quote.expectedContractDurationMonths != null
                ? `${quote.expectedContractDurationMonths}개월`
                : '–'
            }
          />
          <KV k="희망 도입일" v={kstYmd(quote.expectedCloseAt) ?? '–'} />
        </div>
      </div>
    </section>
  );
}

// R2 후속 #5 — '배정 로봇'(예약) + '배치'(Deployment) 두 카드를 '로봇 정보' 한
// 카드로 통합한다. 예약(미배치) 로봇과 배치된 로봇을 한 표에 '상태' 컬럼으로
// 구분. (기존 '배치' 카드의 '유형' 컬럼 제거.)
//
//   예약(reserved) = robotsApi.list({reservedForCustomerId}) 중 아직 배치 전(non-DEPLOYED).
//   배치(deployed) = deploymentsApi.list({customerId}). DEPLOYED 예약 로봇은 배치 행과
//                    중복되므로 예약 쪽에서 제외(dedupe).
//
// deployments.list 가 store(name/branchName/address)를 include(R2 후속 #2)하므로
// 매장명은 별도 stores 조회 없이 d.store 에서 바로 쓴다.
// round4 #2 — '구분 사양' = Robot.variantValue, 라벨 = Model.variantAxisLabel
// (모델별 축이 다름: GEN2=방향 등). 라벨이 있으면 '라벨: 값'으로 병기.
function formatSpec(value: string | null | undefined, label: string | null | undefined): string {
  if (!value) return '–';
  return label ? `${label}: ${value}` : value;
}

function RobotInfoSection({ customerId }: { customerId: string }) {
  const depQ = useQuery({
    queryKey: ['deployments', { customerId }],
    queryFn: () => deploymentsApi.list({ customerId, pageSize: 100 }),
  });
  const reservedQ = useQuery({
    queryKey: ['robots', { reservedForCustomerId: customerId, pageSize: 100 }],
    queryFn: () => robotsApi.list({ reservedForCustomerId: customerId, pageSize: 100 }),
    staleTime: 10_000,
  });

  const isLoading = depQ.isLoading || reservedQ.isLoading;
  const error = depQ.error ?? reservedQ.error;

  // 계획서(2026-08-27) 요구 2 — 행에서 바로 회수·교체. 종전엔 이 카드가 **보기
  // 전용**이라 로봇 하나를 회수하려면 로봇 상세로 하나씩 이동해야 했다.
  const canOperateRow = useAuthStore((st) => (st.user ? canEditAny(st.user.role) : false));
  const [returnFor, setReturnFor] = useState<{ robotId: string } | null>(null);
  // 교체 창이 후보를 이 배치의 법인에 **쓸 수 있는** 로봇으로 좁힌다(SwapRobotModal 참고) —
  // 오너 결정 2026-10-21 부터 비욘드허니컴 배치에는 허니컴로보틱스 로봇도 후보다.
  const [swapFor, setSwapFor] = useState<{
    deploymentId: string;
    label: string;
    ownerEntity: OwnerEntity;
  } | null>(null);
  // 2026-10-20 오너 — 잘못 배치한 로봇 되돌리기(경고창으로 한 번 더 확인). 회수와 달리 '나가지 않았다'로
  // 만든다: 로봇은 운영 중에서 빠져 배치 전 상태로, 교체로 들어간 로봇이면 뺐던 로봇이 다시 운영 중으로.
  const [undoFor, setUndoFor] = useState<{ deploymentId: string; label: string } | null>(null);
  // 2026-10-21 오너 — "이미 회수된 거도 되돌리는 기능 있어야 해". 회수됨 줄의 [되돌리기]는 회수를 없던 일로.
  const [undoReturnFor, setUndoReturnFor] = useState<{
    deploymentId: string;
    label: string;
  } | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  // 일괄 회수 선택 — deploymentId 가 아니라 **robotId** 를 담는다. 회수 창이
  // robotId 로 활성 배치를 스스로 찾기 때문이고, 그래야 한 벌의 창을 그대로 쓴다.
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  // 회수된 기록 펼침(2026-10-28 오너 "정리") — 처음엔 접어 둔다.
  const [showHistory, setShowHistory] = useState(false);

  const deployments = depQ.data?.items ?? [];
  // 오너 실사고(2026-09-10) — 종전엔 `status !== DEPLOYED` 로 걸렀다. 그 필터의 전제는
  // "운영중인 예약 로봇은 아래 배치 목록에 반드시 있다"였는데, **배치 없이 운영중인
  // 로봇**(상태만 손으로 바꾼 고아 DEPLOYED)에서 그 전제가 깨진다. 그러면 배치 목록에도
  // 없고 예약 목록에서도 걸러져 **두 목록 모두에서 사라진다** — 실제로 #245 가 그렇게
  // 사라졌고, 행이 0이 되면서 카드 자체가 없어져 "배정을 했는지"조차 알 수 없었다.
  //
  // 그래서 상태가 아니라 **이 고객사 배치 목록에 있는가**로 중복을 제거한다. 목적(중복
  // 제거)은 그대로 달성하고, 배치가 어디에도 없는 로봇은 사라지지 않는다.
  const deployedRobotIds = new Set(deployments.map((d) => d.robotId));
  const reserved = (reservedQ.data?.items ?? []).filter((r) => !deployedRobotIds.has(r.id));

  // ── 개선 9건 차수2e(26-08-04) ㉢ — 이 로봇이 **어느 계약의 것인가** ────────────────
  //
  // 왜 필요했나 — 고객사에 계약이 2건이 되자 이 표에 두 계약의 로봇이 라벨 없이 섞였다.
  // "이 로봇이 구독 계약 것인지 새 판매 계약 것인지"를 화면에서 알 방법이 없었다.
  //
  // 어디서 알 수 있나 — 배치 응답(deployments.list)에는 계약이 **없다**(Deployment 에
  // contractId 컬럼이 없다 — schema.prisma). 계약↔로봇을 잇는 유일한 정본은 로봇의 예약
  // 계약(Robot.reservedContractId)이고, 서버가 계약의 도입일·설치 완료를 계산할 때도 정확히
  // 그 값으로 좁힌다(cascade-deploy.helper / customer-master.row
  // resolveContractInstallCompletedAt). 그래서 표시층도 같은 값을 본다.
  //
  // 예약 조회는 배치 여부로 걸러지지 않으므로(robots.service: reservedContract.customerId
  // 로만 join) 이미 배치된 로봇의 예약 계약도 함께 온다 → 배치 행의 계약도 이 지도에서 찾는다.
  // ⚠ 예약이 걸리지 않은 옛 배치 로봇은 계약을 알 수 없다 → '–'. 다른 계약 번호를
  //   집어오지 않는다(이번 결함의 본질이 그것이다).
  const contractByRobotId = new Map<string, string>();
  for (const r of reservedQ.data?.items ?? []) {
    if (r.reservedContract?.humanId) contractByRobotId.set(r.id, r.reservedContract.humanId);
  }

  const rows: Array<{
    key: string;
    robotId: string;
    sn: string;
    model: string;
    // ㉢ — 이 로봇의 예약 계약 번호. null = 알 수 없음(예약 없음).
    contractHumanId: string | null;
    // round4 #2 — '매장' 제거 + '구분 사양'(variantValue) 추가.
    spec: string;
    installAt: string | null;
    // 개선계획 §7 — '회수됨' 배지만으로는 언제 회수했는지 알 수 없었다.
    returnedAt: string | null;
    status: React.ReactNode;
    // 계획서(2026-08-27) 요구 2 — 행에서 바로 회수·교체할 수 있게.
    // deploymentId 가 있고 아직 회수 전인 행만 동작 대상이다(예약 행은 배치가 없다).
    deploymentId: string | null;
    // 회수된 배치 — [되돌리기](회수 취소) 대상.
    returnedDeploymentId: string | null;
    // 오너 결정 2026-10-20 — 교체 후보를 고를 법인. 서버 교체 규칙이 견주는 값은 고객사가
    // 아니라 **배치 기록의 법인**이라 그것을 그대로 넘긴다. 예약 행은 배치가 없어 null.
    deploymentOwnerEntity: OwnerEntity | null;
    // 2026-10-26 — 스페어(예비) 로봇 배치. 고객 배치(나가 있는 것)만 [스페어로] 를 켤 수 있다.
    isSpare: boolean;
    canMarkSpare: boolean;
  }> = [
    // 예약(미배치) 먼저 — 아직 현장에 배치되지 않은 단위.
    ...reserved.map((r) => ({
      key: `res-${r.id}`,
      robotId: r.id,
      // round3 #4 — Short SN 우선(배치 분기 및 앱 전체 표준과 동일). 기존엔
      // serialLong(Long SN) 우선이라 예약 로봇만 Long SN 이 표시되던 버그.
      sn: r.serialShort
        ? formatRobotShortId(r.serialShort)
        : (r.serialLong ?? `${r.id.slice(0, 8)}…`),
      model: r.model.displayName,
      contractHumanId: r.reservedContract?.humanId ?? null,
      spec: formatSpec(r.variantValue, r.model.variantAxisLabel),
      installAt: null,
      returnedAt: null,
      // 오너 실사고(2026-09-10) — 상태가 '운영중'인데 이 고객사 배치가 없는 행은
      // 그냥 '운영중'으로 그리면 **거짓말**이다(어디서 운영 중인지 아무도 모른다).
      // 그 사실을 그 자리에서 말한다 — 조용히 넘기면 이번 사고가 반복된다.
      status:
        r.status === RobotStatus.DEPLOYED ? (
          <span
            data-testid="robot-orphan-deployed"
            style={{ color: 'var(--fg-danger)', fontWeight: 600 }}
            title="이 고객사에 배치 기록이 없습니다. 다른 곳에 배치돼 있거나, 배치 없이 상태만 '운영중'으로 바뀐 로봇입니다. 로봇 상세에서 [배치]로 고객사·매장에 배치해 주세요."
          >
            운영중 · 배치 기록 없음
          </span>
        ) : (
          <RobotStatusPill status={r.status} />
        ),
      // 예약(미배치)은 아직 나간 적이 없다 — 회수·교체할 배치가 없다.
      deploymentId: null,
      returnedDeploymentId: null,
      deploymentOwnerEntity: null,
      isSpare: false,
      canMarkSpare: false,
    })),
    // 배치된 로봇.
    ...deployments.map((d) => ({
      key: `dep-${d.id}`,
      robotId: d.robotId,
      sn: d.robot?.serialShort
        ? formatRobotShortId(d.robot.serialShort)
        : (d.robot?.serialLong ?? `${d.robotId.slice(0, 8)}…`),
      model: d.robot?.model?.code ?? '–',
      contractHumanId: contractByRobotId.get(d.robotId) ?? null,
      spec: formatSpec(d.robot?.variantValue, d.robot?.model?.variantAxisLabel),
      installAt: d.deployedAt,
      returnedAt: d.returnedAt,
      // 2026-09-23 — 고객 배치만 '운영 중' 이다(오너 결정 2026-09-18). 규칙은 네 화면이
      // 같이 쓰는 한 벌(deployment-status-label)에 있다.
      status: <DeploymentStatusCell kind={d.kind} returnedAt={d.returnedAt} />,
      deploymentId: d.returnedAt ? null : d.id,
      returnedDeploymentId: d.returnedAt ? d.id : null,
      deploymentOwnerEntity: d.ownerEntity,
      isSpare: d.isSpare,
      canMarkSpare: !d.returnedAt && d.kind === DeploymentKind.CUSTOMER,
    })),
  ];

  const selected = rows.filter((r) => r.deploymentId && checked.has(r.robotId));

  // 2026-10-26 오너 — "#2103이 스페어용. 콘솔에 표시해두자". 표시만 바꾼다(로봇 · 계약은 그대로).
  const spareM = useMutation({
    mutationFn: (v: { deploymentId: string; isSpare: boolean; label: string }) =>
      deploymentsApi.setSpare(v.deploymentId, v.isSpare),
    onSuccess: (_res, v) => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      qc.invalidateQueries({ queryKey: ['robot-deployments'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(
        v.isSpare
          ? `${v.label} 로봇을 스페어로 전환했습니다. 이제 설치 대수에 포함되지 않습니다.`
          : `${v.label} 로봇의 스페어 지정을 해제했습니다.`,
      );
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // 2026-10-28 오너 "이거나 정리좀하자" — 지금 나가 있는(또는 배정된) 로봇만 위 표에 두고, 회수된 기록은 아래로
  // 접어 둔다. 종전엔 회수된 줄이 운영 중인 줄 사이에 섞여(같은 로봇이 두세 줄) 지금 몇 대가 나가 있는지 읽기 어려웠다.
  const current = rows.filter((r) => !r.returnedAt);
  const history = rows.filter((r) => r.returnedAt);

  // 오너 실사고(2026-09-10) — 종전엔 행이 0이면 **카드를 통째로 없앴다**. 그래서
  // "배정을 안 했나? 화면이 고장인가?"를 구분할 수 없었다(오너가 실제로 그 상태에
  // 부딪혔다). 0건도 사실이므로 그 사실을 말한다 — 조용히 사라지지 않는다.
  if (!isLoading && !error && rows.length === 0) {
    return (
      <section>
        <SectionHeader title="로봇 정보" count={0} />
        <div style={card}>
          <div style={loadingInline} data-testid="robot-info-empty">
            이 고객사에 배정하거나 배치한 로봇이 없습니다. 계약에 로봇을 배정한 뒤 설치 완료를
            처리하면 여기에 나타납니다.
          </div>
        </div>
      </section>
    );
  }
  return (
    <section>
      <SectionHeader title="로봇 정보" count={current.length} />
      {/* 일괄 회수 — 고른 로봇을 **한 대씩 차례로** 회수 창에 올린다.
          한 번에 서버로 몰아보내지 않는 이유: 회수는 사유·처분·장소가 로봇마다
          다를 수 있고(고장 교체 vs 계약 만료), 무엇보다 **어느 대가 실패했는지**를
          운영자가 그 자리에서 알아야 한다. 창이 하나씩 뜨면 실패한 대에서 멈춘다. */}
      {canOperateRow && selected.length > 0 && (
        <div style={bulkBar} data-testid="robot-bulk-bar">
          <span>{selected.length}대 선택됨</span>
          <Button
            data-testid="robot-bulk-return"
            onClick={() => setReturnFor({ robotId: selected[0]!.robotId })}
          >
            선택한 로봇 회수
          </Button>
          <Button variant="secondary" onClick={() => setChecked(new Set())}>
            선택 해제
          </Button>
        </div>
      )}
      <div style={card}>
        {isLoading ? (
          <div style={loadingInline}>불러오는 중…</div>
        ) : error ? (
          <div style={{ color: 'var(--fg-danger)' }}>{extractApiError(error)}</div>
        ) : (
          <>
            {current.length === 0 ? (
              <div style={loadingInline} data-testid="robot-info-none-active">
                지금 나가 있거나 배정된 로봇이 없습니다. 지난 기록은 아래에서 볼 수 있습니다.
              </div>
            ) : (
              <table style={{ ...tbl, minWidth: 'max-content' }}>
                <thead>
                  <tr>
                    {canOperateRow && <th style={{ ...rth, width: 28 }} aria-label="선택" />}
                    <th style={rth}>로봇</th>
                    {/* 개선 9건 차수2e(26-08-04) ㉢ — 두 계약의 로봇이 라벨 없이 섞이지 않게.
                        값의 출처와 '–' 의 뜻은 위 contractByRobotId 주석에 있다. */}
                    <th style={rth}>계약</th>
                    <th style={rth}>모델</th>
                    <th style={rth}>구분 사양</th>
                    {/* round11 (요구 2) — 값은 Deployment.deployedAt(배치일). 계약의 '도입일'과 다르다. */}
                    <th style={rth}>배치일</th>
                    <th style={rth}>상태</th>
                    {canOperateRow && <th style={rth}>동작</th>}
                  </tr>
                </thead>
                <tbody>
                  {current.map((r) => (
                    <tr key={r.key} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      {canOperateRow && (
                        <td style={rtd}>
                          {/* 회수·교체할 수 있는 행(나가 있는 배치)만 고를 수 있다. */}
                          {r.deploymentId && (
                            <input
                              type="checkbox"
                              aria-label={`${r.sn} 선택`}
                              data-testid={`robot-row-check-${r.robotId}`}
                              checked={checked.has(r.robotId)}
                              onChange={(e) => {
                                setChecked((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(r.robotId);
                                  else next.delete(r.robotId);
                                  return next;
                                });
                              }}
                            />
                          )}
                        </td>
                      )}
                      <td style={{ ...rtd, fontFamily: 'var(--font-mono)' }}>
                        <Link to={`/robots/${r.robotId}`} style={linkStyle}>
                          {r.sn}
                        </Link>
                        {r.isSpare && <SpareBadge testId={`robot-row-spare-badge-${r.robotId}`} />}
                      </td>
                      <td style={{ ...rtd, fontFamily: 'var(--font-mono)' }}>
                        {r.contractHumanId ?? <span style={{ color: 'var(--fg-muted)' }}>–</span>}
                      </td>
                      <td style={rtd}>{r.model}</td>
                      <td style={rtd}>
                        {r.spec === '–' ? (
                          <span style={{ color: 'var(--fg-muted)' }}>–</span>
                        ) : (
                          r.spec
                        )}
                      </td>
                      <td style={rtd}>{kstYmd(r.installAt) ?? '–'}</td>
                      <td style={rtd}>{r.status}</td>
                      {canOperateRow && (
                        <td style={rtd}>
                          {r.deploymentId ? (
                            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                              <Button
                                variant="secondary"
                                style={rowBtn}
                                data-testid={`robot-row-return-${r.robotId}`}
                                onClick={() => setReturnFor({ robotId: r.robotId })}
                              >
                                회수
                              </Button>
                              <Button
                                variant="secondary"
                                style={rowBtn}
                                data-testid={`robot-row-swap-${r.robotId}`}
                                onClick={() =>
                                  setSwapFor({
                                    deploymentId: r.deploymentId!,
                                    label: r.sn,
                                    ownerEntity: r.deploymentOwnerEntity!,
                                  })
                                }
                              >
                                교체
                              </Button>
                              {/* 자주 안 쓰는 동작은 '⋯' 안에(2026-10-28 오너 "정리") — 버튼이 넷이면 표가 옆으로 밀렸다. */}
                              <RowMoreMenu
                                label={`${r.sn} 동작 더 보기`}
                                testId={`robot-row-more-${r.robotId}`}
                                items={[
                                  ...(r.canMarkSpare
                                    ? [
                                        {
                                          label: r.isSpare ? '스페어 해제' : '스페어로 전환',
                                          testId: `robot-row-spare-${r.robotId}`,
                                          disabled: spareM.isPending,
                                          title: r.isSpare
                                            ? '스페어 지정을 해제하면 다시 설치 대수에 포함됩니다.'
                                            : '계약 대수와 별도로 둔 예비 로봇이면 스페어로 전환하세요. 배치는 그대로 두고 설치 대수에서만 뺍니다.',
                                          onClick: () =>
                                            spareM.mutate({
                                              deploymentId: r.deploymentId!,
                                              isSpare: !r.isSpare,
                                              label: r.sn,
                                            }),
                                        },
                                      ]
                                    : []),
                                  {
                                    label: '배치 되돌리기',
                                    testId: `robot-row-undo-${r.robotId}`,
                                    title: '잘못 배치했을 때 배치하기 전 상태로 되돌립니다',
                                    danger: true,
                                    onClick: () =>
                                      setUndoFor({ deploymentId: r.deploymentId!, label: r.sn }),
                                  },
                                ]}
                              />
                            </span>
                          ) : (
                            <span style={{ color: 'var(--fg-muted)' }}>–</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {history.length > 0 && (
              <div style={{ marginTop: current.length === 0 ? 8 : 12 }}>
                <button
                  type="button"
                  style={historyToggle}
                  aria-expanded={showHistory}
                  data-testid="robot-history-toggle"
                  onClick={() => setShowHistory((v) => !v)}
                >
                  <Icon name={showHistory ? 'chev-up' : 'chev-down'} size={14} aria-hidden />
                  회수된 기록 {history.length}건 {showHistory ? '접기' : '보기'}
                </button>
                {showHistory && (
                  <table
                    style={{ ...tbl, minWidth: 'max-content', marginTop: 6 }}
                    data-testid="robot-history-table"
                  >
                    <thead>
                      <tr>
                        <th style={rth}>로봇</th>
                        <th style={rth}>계약</th>
                        <th style={rth}>모델</th>
                        <th style={rth}>구분 사양</th>
                        <th style={rth}>배치일</th>
                        <th style={rth}>회수일</th>
                        {canOperateRow && <th style={rth}>동작</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((r) => (
                        <tr
                          key={r.key}
                          style={{
                            borderTop: '1px solid var(--border-subtle)',
                            color: 'var(--fg-muted)',
                          }}
                        >
                          <td style={{ ...rtd, fontFamily: 'var(--font-mono)' }}>
                            <Link to={`/robots/${r.robotId}`} style={linkStyle}>
                              {r.sn}
                            </Link>
                          </td>
                          <td style={{ ...rtd, fontFamily: 'var(--font-mono)' }}>
                            {r.contractHumanId ?? '–'}
                          </td>
                          <td style={rtd}>{r.model}</td>
                          <td style={rtd}>{r.spec}</td>
                          <td style={rtd}>{kstYmd(r.installAt) ?? '–'}</td>
                          <td style={rtd}>{kstYmd(r.returnedAt) ?? '–'}</td>
                          {canOperateRow && (
                            <td style={rtd}>
                              {r.returnedDeploymentId ? (
                                <Button
                                  variant="secondary"
                                  style={rowBtn}
                                  data-testid={`robot-row-undo-return-${r.robotId}`}
                                  title="잘못 회수했을 때 회수하기 전의 운영 중 상태로 되돌립니다"
                                  onClick={() =>
                                    setUndoReturnFor({
                                      deploymentId: r.returnedDeploymentId!,
                                      label: r.sn,
                                    })
                                  }
                                >
                                  회수 되돌리기
                                </Button>
                              ) : (
                                '–'
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {returnFor && (
        <ReturnRobotModal
          open
          robotId={returnFor.robotId}
          onClose={() => {
            // 회수한 로봇은 선택에서 뺀다 — 남겨 두면 이미 회수한 대를 다시 고른다.
            setChecked((prev) => {
              const next = new Set(prev);
              next.delete(returnFor.robotId);
              return next;
            });
            setReturnFor(null);
          }}
        />
      )}
      {undoFor && (
        <ConfirmDialog
          open
          title="배치 되돌리기"
          message={[
            `${undoFor.label} 로봇의 배치를 되돌립니다.`,
            '',
            // 2026-10-20 오너 — 문단끼리 띄워 쓴다(가독성).
            '· 이 로봇은 운영 중에서 빠져 출고 대기나 설치 대기 같은 배치 전 상태로 돌아가고, 이 고객사 로봇 목록에서도 빠집니다.',
            '',
            '· 교체로 들어간 로봇이면 그때 뺐던 로봇이 다시 이 매장에 운영 중으로 돌아오고, 계약도 그 로봇으로 돌아갑니다. 고장 교체로 남은 고장 조치 이력도 함께 지웁니다.',
            '',
            '· 회수와 달리 회수 기록과 정비 오더는 남지 않습니다. 되돌린 사실은 감사 로그에 남습니다.',
            '',
            '잘못 배치한 경우에만 쓰세요. 실제로 로봇을 가져왔다면 [회수]를 쓰세요.',
          ].join('\n')}
          confirmLabel="되돌리기"
          danger
          onConfirm={async () => {
            try {
              const res = await deploymentsApi.undo(undoFor.deploymentId, { reason: '잘못 배치' });
              await Promise.all([
                qc.invalidateQueries({ queryKey: ['deployments'] }),
                qc.invalidateQueries({ queryKey: ['robots'] }),
                qc.invalidateQueries({ queryKey: ['customer'] }),
                qc.invalidateQueries({ queryKey: ['customer-master'] }),
                qc.invalidateQueries({ queryKey: ['dashboard'] }),
              ]);
              toast.success(
                res.restoredDeploymentId
                  ? `${undoFor.label} 배치를 되돌렸습니다. 교체 전 로봇이 다시 운영 중입니다.`
                  : `${undoFor.label} 배치를 되돌렸습니다. 운영 중에서 빠졌습니다.`,
              );
              setUndoFor(null);
            } catch (err) {
              throw new Error(extractApiError(err));
            }
          }}
          onCancel={() => setUndoFor(null)}
        />
      )}
      {undoReturnFor && (
        <ConfirmDialog
          open
          title="회수 되돌리기"
          message={[
            `${undoReturnFor.label} 로봇의 회수를 되돌립니다.`,
            '',
            '· 이 로봇은 다시 이 매장에 운영 중으로 돌아가고, 회수일이 지워집니다.',
            '',
            '· 회수 때 풀린 계약 배정과, 회수로 종료된 고객사도 회수 전으로 돌아갑니다.',
            '',
            '· 회수로 생긴 고장 조치 이력은 지웁니다. 회수 정비 오더는 아직 아무도 손대지 않았으면 취소하고, 진행 중이면 남겨 둡니다.',
            '',
            '잘못 회수한 경우에만 쓰세요. 되돌린 사실은 감사 로그에 남습니다.',
          ].join('\n')}
          confirmLabel="되돌리기"
          danger
          onConfirm={async () => {
            try {
              const res = await deploymentsApi.undoReturn(undoReturnFor.deploymentId, {
                reason: '잘못 회수',
              });
              await Promise.all([
                qc.invalidateQueries({ queryKey: ['deployments'] }),
                qc.invalidateQueries({ queryKey: ['robots'] }),
                qc.invalidateQueries({ queryKey: ['customer'] }),
                qc.invalidateQueries({ queryKey: ['customer-master'] }),
                qc.invalidateQueries({ queryKey: ['service-orders'] }),
                qc.invalidateQueries({ queryKey: ['dashboard'] }),
              ]);
              const extra = [
                res.customerReopened ? '고객사를 다시 열었습니다' : null,
                res.cancelledMaintenanceOrder
                  ? `정비 오더 ${res.cancelledMaintenanceOrder}를 취소했습니다`
                  : null,
                res.keptMaintenanceOrder
                  ? `정비 오더 ${res.keptMaintenanceOrder}는 진행 중이라 남겨 두었습니다`
                  : null,
              ].filter(Boolean);
              toast.success(
                `${undoReturnFor.label} 회수를 되돌렸습니다. 다시 운영 중입니다.${
                  extra.length ? ` ${extra.join('. ')}.` : ''
                }`,
              );
              setUndoReturnFor(null);
            } catch (err) {
              throw new Error(extractApiError(err));
            }
          }}
          onCancel={() => setUndoReturnFor(null)}
        />
      )}
      {swapFor && (
        <SwapRobotModal
          open
          deploymentId={swapFor.deploymentId}
          currentRobotLabel={swapFor.label}
          customerId={customerId}
          ownerEntity={swapFor.ownerEntity}
          onClose={() => setSwapFor(null)}
        />
      )}
    </section>
  );
}

function CustomerMasterSpecSection({ customerId }: { customerId: string }) {
  const q = useQuery({
    queryKey: ['customer-master', 'one', customerId],
    queryFn: () => customerMasterApi.get(customerId),
    staleTime: 30_000,
  });
  if (!q.data) return null;
  const row = q.data;
  const rentalCompanyDisplay =
    row.rentalCompany ?? (row.hasAJ === true ? 'AJ' : row.hasAJ === false ? '해당없음' : null);
  const robots = row.robots ?? [];
  function joinRobotField(
    field: 'setting' | 'grillPlate' | 'specialParts' | 'wireGrill' | 'storeId',
  ): string | null {
    const vals = robots.map((r) => r[field]?.trim()).filter((v): v is string => Boolean(v));
    const unique = [...new Set(vals)];
    return unique.length > 0 ? unique.join(' / ') : null;
  }
  // 2026-09-30 오너 보고 — 등록·편집 창의 '#n 로봇 Store ID'(ContractLine.storeId)가 이
  // 칸 목록에 없어 저장해도 상세 화면 어디에도 안 보였다. 같은 창 같은 줄의 형제 칸
  // (특수부위 · 그릴 종류)과 같은 규칙으로 보인다.
  //
  // 옛 '프로파일'(Customer.profileConfig)은 같은 뜻의 **예전 고객사 단위 Store ID** 다
  // (schema 주석 "Store ID / 한정판 등", 2026-05-30 줄 단위로 바뀌며 창에서 칸이 사라짐).
  // 다른 이름으로 같은 것이 두 번 보이지 않게, 줄 값이 하나도 없을 때만 옛 값을 보인다 —
  // 줄 값이 생기면 그것이 옛 값을 대체한 것이다(옛 값은 이제 어느 화면에서도 못 고친다).
  const lineStoreIds = joinRobotField('storeId');
  const legacyStoreId = lineStoreIds ? null : row.profileConfig?.trim() || null;
  const entries: Array<[string, string | null]> = [
    ['담당자', row.primaryContactName],
    ['직함', row.primaryContactPosition],
    ['연락처', row.primaryContactPhone],
    ['이메일', row.primaryContactEmail],
    ['렌탈사', rentalCompanyDisplay],
    ['가스 타입', row.grillSelector.type],
    ['가스 설치', row.grillSelector.installStatus],
    ['세팅 메뉴', joinRobotField('setting')],
    ['불판', joinRobotField('grillPlate')],
    ['특수부위', joinRobotField('specialParts')],
    ['와이어 그릴', joinRobotField('wireGrill')],
    // 창의 줄 순서(특수부위 · 그릴 종류 · Store ID)를 따른다.
    ['Store ID', lineStoreIds],
    ['Store ID (이전 입력)', legacyStoreId],
  ];
  const shown = entries.filter(([, v]) => v != null && v !== '');
  const att = row.attachments;
  const attTotal = att.install + att.selector + att.training;
  if (shown.length === 0 && attTotal === 0) return null;
  return (
    <section>
      <SectionHeader title="주요 사양 · 담당자" count={shown.length + (attTotal > 0 ? 1 : 0)} />
      <div style={card}>
        <div style={kvGridSpec}>
          {shown.map(([k, v]) => (
            <div key={k}>
              <div style={kSpec}>{k}</div>
              <div style={vSpec}>{v}</div>
            </div>
          ))}
          {attTotal > 0 && (
            <div>
              <div style={kSpec}>첨부</div>
              <div style={vSpec}>
                설치 {att.install} · 가스 {att.selector} · 인수교육 {att.training}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// round10 요구1 — CRM 접촉 메모 타임라인(좌하단).
//
// '비고'(Customer.notes, 단일 편집)와는 별개인 다건 접촉 로그.
// 쓰기는 인증된 모든 역할(개별 영업 포함, [D11=A]) — @Roles 없이 백엔드가 열어둠.
// 수정·삭제 버튼은 작성자 본인 또는 ADMIN 에게만 노출(백엔드에서도 재검증).
// 추가수정 2차(26-07-27) §2 — 접촉 메모 시각을 KST 고정으로. 예전에는 PC
// 시간대를 따라가 해외에서 열면 다른 시각이 보였고 기준 표시도 없었다.
// 타임라인은 상세 화면 안이지만 '작성자 · 시각' 캡션이 줄마다 반복되므로
// '(한국 시간)' 라벨은 붙이지 않는다(캡션이 두 배로 길어짐). 같은 카드
// 위쪽 '등록일' KV 가 라벨을 달고 있어 기준은 화면에서 한 번 드러난다.
function formatNoteTime(iso: string): string {
  return kstDateTime(iso) ?? '';
}

function CrmTimelineSection({ customerId }: { customerId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const myId = useAuthStore((s) => s.user?.id);
  const myRole = useAuthStore((s) => s.user?.role);
  const [draft, setDraft] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const q = useQuery({
    queryKey: ['customer-notes', customerId],
    queryFn: () => customerNotesApi.list(customerId),
    staleTime: 30_000,
  });

  const createMx = useMutation({
    mutationFn: (body: string) => customerNotesApi.create(customerId, body),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['customer-notes', customerId] });
    },
    onError: (err) => toast.error(extractApiError(err), 'CRM 메모 저장 실패'),
  });

  const updateMx = useMutation({
    mutationFn: ({ noteId, body }: { noteId: string; body: string }) =>
      customerNotesApi.update(noteId, body),
    onSuccess: () => {
      setEditTarget(null);
      setEditDraft('');
      qc.invalidateQueries({ queryKey: ['customer-notes', customerId] });
    },
    onError: (err) => toast.error(extractApiError(err), 'CRM 메모 수정 실패'),
  });

  const deleteMx = useMutation({
    mutationFn: (noteId: string) => customerNotesApi.remove(noteId),
    onSuccess: () => {
      setDeleteTarget(null);
      qc.invalidateQueries({ queryKey: ['customer-notes', customerId] });
    },
    onError: (err) => {
      setDeleteTarget(null);
      toast.error(extractApiError(err), 'CRM 메모 삭제 실패');
    },
  });

  const notes = q.data ?? [];

  function submit() {
    const body = draft.trim();
    if (!body || createMx.isPending) return;
    createMx.mutate(body);
  }

  function submitEdit() {
    const body = editDraft.trim();
    if (!editTarget || !body || updateMx.isPending) return;
    updateMx.mutate({ noteId: editTarget, body });
  }

  return (
    <section>
      <SectionHeader title="CRM 메모" count={notes.length} />
      <div style={card}>
        {/* 입력창 — Ctrl/⌘+Enter 로 빠른 저장. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            marginBottom: notes.length ? 12 : 0,
          }}
        >
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="통화, 방문, 이메일 같은 접촉 내용을 적어 주세요. ⌘/Ctrl+Enter로 저장합니다."
            rows={2}
            maxLength={2000}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={submit} disabled={!draft.trim() || createMx.isPending}>
              {createMx.isPending ? '저장 중…' : '메모 추가'}
            </Button>
          </div>
        </div>

        {/* 타임라인 */}
        {q.isLoading ? (
          <div style={loadingInline}>불러오는 중…</div>
        ) : q.error ? (
          <div style={{ color: 'var(--fg-danger)' }}>{extractApiError(q.error)}</div>
        ) : notes.length === 0 ? (
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            아직 메모가 없습니다. 첫 접촉 기록을 남겨 보세요.
          </div>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {notes.map((n) => {
              const canWrite =
                (n.authorUserId != null && n.authorUserId === myId) || myRole === 'ADMIN';
              const editing = editTarget === n.id;
              return (
                <li key={n.id} style={crmNoteItem}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 8,
                    }}
                  >
                    <span
                      className="kuun-caption"
                      style={{ color: 'var(--fg-muted)', fontSize: 11 }}
                    >
                      {n.authorName ?? '(삭제된 사용자)'} · {formatNoteTime(n.createdAt)}
                      {n.updatedAt ? ' · 수정됨' : ''}
                    </span>
                    {canWrite && !editing && (
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => {
                            setEditTarget(n.id);
                            setEditDraft(n.body);
                          }}
                          style={crmIconBtn}
                          aria-label="메모 수정"
                          title="수정"
                        >
                          <Icon name="pen" size={13} aria-hidden />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(n.id)}
                          style={crmIconBtn}
                          aria-label="메모 삭제"
                          title="삭제"
                        >
                          <Icon name="trash" size={13} aria-hidden />
                        </button>
                      </div>
                    )}
                  </div>
                  {editing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                      <Textarea
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={2}
                        maxLength={2000}
                        autoFocus
                        onKeyDown={(e) => {
                          if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                            e.preventDefault();
                            submitEdit();
                          }
                          if (e.key === 'Escape') setEditTarget(null);
                        }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                        <Button
                          variant="secondary"
                          onClick={() => setEditTarget(null)}
                          disabled={updateMx.isPending}
                        >
                          취소
                        </Button>
                        <Button
                          onClick={submitEdit}
                          disabled={!editDraft.trim() || updateMx.isPending}
                        >
                          {updateMx.isPending ? '저장 중…' : '저장'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {n.body}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="CRM 메모 삭제"
        message="이 메모를 삭제하시겠습니까? 되돌릴 수 없습니다."
        confirmLabel="삭제"
        busy={deleteMx.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMx.mutate(deleteTarget);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </section>
  );
}

// 인프라 발주 섹션 — 5건 preview + 더 보기.
function InfrastructureSection({
  customerId,
  canRequest,
}: {
  customerId: string;
  canRequest: boolean;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['infrastructure-orders', { customerId }],
    queryFn: () => infrastructureOrdersApi.list({ customerId }),
    staleTime: 30_000,
  });

  // S1 Phase 7 (R2 #6) — '연락 요청' 버튼. 필드팀에 INFRA_CONTACT_REQUESTED
  // 알람 발송. order.contactedAt == null 일 때만 노출.
  const requestContact = useMutation({
    mutationFn: (orderId: string) => infrastructureOrdersApi.requestContact(orderId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('필드팀에 연락 요청을 보냈습니다.');
    },
    onError: (err) => {
      toast.error(extractApiError(err), '연락 요청 실패');
    },
  });

  const items = q.data ?? [];
  // S1 Phase 7 — 5건 preview 패턴 적용. spec §3.1 wireframe.
  const previewItems = items.slice(0, SECTION_PREVIEW_LIMIT);
  const showMore = items.length > SECTION_PREVIEW_LIMIT;
  return (
    <section>
      <SectionHeader
        title="인프라 발주"
        count={items.length}
        moreHref={`/infrastructure?customerId=${customerId}`}
        showMore={showMore}
      />
      <div style={card}>
        {q.isLoading ? (
          <div style={loadingInline}>불러오는 중…</div>
        ) : q.error ? (
          <div style={{ color: 'var(--fg-danger)' }}>{extractApiError(q.error)}</div>
        ) : items.length === 0 ? (
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            등록된 인프라 발주가 없습니다. 계약 사인 또는 진행 상태=계약 완료 전이 시 자동
            생성됩니다.
          </div>
        ) : (
          <table style={tbl}>
            <thead>
              <tr>
                {/* windows-ux-edit Round 2 Phase C (#1) — 매장 열 제거.
                    개선 9건 차수2e(26-08-04) ㉢ — '계약' 열은 **되돌렸다**. 그때는 계약이
                    1건이라 계약 번호가 카드에 한 번 있으면 충분했지만, 추가 판매를 새 계약으로
                    유도한 뒤로는 한 고객사의 발주가 여러 계약에 걸린다 — 라벨이 없으면 어느
                    계약의 발주인지 알 수 없다. 값은 발주 응답이 실어 주는 계약번호를 그대로
                    쓴다(infrastructure-orders 의 contract.humanId — 서버 include). */}
                <th style={{ ...th, textAlign: 'center' }}>계약</th>
                <th style={{ ...th, textAlign: 'center' }}>가스</th>
                <th style={{ ...th, textAlign: 'center' }}>연락</th>
                <th style={{ ...th, textAlign: 'center' }}>발주</th>
                <th style={{ ...th, textAlign: 'center' }}>납품</th>
                <th style={{ ...th, textAlign: 'center' }}>설치 예정</th>
                <th style={{ ...th, textAlign: 'center' }}>완료</th>
                {canRequest && <th style={{ ...th, textAlign: 'center' }} />}
              </tr>
            </thead>
            <tbody>
              {previewItems.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  {/* windows-ux-edit Round 2 Phase C (#1) — 매장 셀 제거.
                      ㉢ — 계약번호가 실려 오지 않으면 '–'. 짐작하지 않는다. */}
                  <td
                    style={{ ...td, textAlign: 'center', fontFamily: 'var(--font-mono)' }}
                    data-testid={`cd-infra-contract-${r.id}`}
                  >
                    {r.contract?.humanId ?? <span style={{ color: 'var(--fg-muted)' }}>–</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    {/* round3 #1 — gasType(enum) 없으면 사업팀 입력 grillSelectorType
                        로 fallback(자체/전기그리들/도시가스 등도 표시). */}
                    {r.gasType === GasType.LPG
                      ? 'LPG'
                      : r.gasType === GasType.LNG
                        ? 'LNG'
                        : r.gasType === GasType.UNDECIDED
                          ? '미정'
                          : (r.grillSelectorType ?? '–')}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <StagePill done={!!r.contactedAt} />
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <StagePill done={!!r.grillOrderedAt} />
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <StagePill done={!!r.grillDeliveredAt} />
                  </td>
                  <td style={{ ...td, textAlign: 'center', fontFamily: 'var(--font-mono)' }}>
                    {kstYmd(r.installScheduledAt) ?? '-'}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <StagePill done={!!r.installCompletedAt} />
                  </td>
                  {canRequest && (
                    <td style={{ ...td, textAlign: 'center' }}>
                      {r.contactedAt == null && (
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => requestContact.mutate(r.id)}
                          disabled={requestContact.isPending}
                        >
                          연락 요청
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function StagePill({ done }: { done: boolean }) {
  return (
    <span
      role="status"
      aria-label={done ? '완료' : '미완료'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 24,
        padding: '2px 8px',
        borderRadius: 'var(--r-pill)',
        fontSize: 11,
        fontWeight: 600,
        background: done ? 'var(--bg-success-subtle)' : 'var(--bg-sunken)',
        color: done ? 'var(--fg-on-success-tint)' : 'var(--fg-muted)',
        border: done
          ? '1px solid var(--border-success-saturated)'
          : '1px solid var(--border-default)',
      }}
    >
      {done ? <Icon name="check" size={12} aria-hidden /> : <span aria-hidden="true">—</span>}
    </span>
  );
}

const kvGridSpec: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
};
const kSpec: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-muted)',
  marginBottom: 4,
};
const vSpec: CSSProperties = {
  fontSize: 13,
  color: 'var(--fg-default)',
};

const mainColumn: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16 };
const twoColInner: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 16,
};
const leftCol: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16 };
const rightCol: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16 };

const statusBadgeBase: CSSProperties = {
  display: 'inline-block',
  padding: '3px 10px',
  borderRadius: 'var(--r-input)',
  fontSize: 12,
  fontWeight: 600,
};
const dDayStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--fg-danger)',
};
// 다계약 표시 차수3(26-08-05) ㉠ — D-Day 자리가 비는 **정상 상태**의 글자(판매 전용).
// 위 D-Day 와 달리 경고 색을 쓰지 않는다 — 지켜볼 반납일이 없는 것은 사고가 아니다.
const dDayNoneStyle: CSSProperties = {
  color: 'var(--fg-muted)',
  fontFamily: 'var(--font-mono)',
};
// 다계약 표시 차수3 정정2(26-08-06) ㉡ — '이 값은 어느 계약 것인가' 라벨. 종전 D-Day 라벨의
// 인라인 스타일 그대로다(값이 하나뿐인 고객사에서 헤더가 종전과 같아야 하므로 글자꼴도 그대로).
const basisLabelStyle: CSSProperties = {
  color: 'var(--fg-muted)',
  fontFamily: 'var(--font-mono)',
};

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 16,
  overflow: 'auto',
};
// 개선 9건 차수2e(26-08-04) ㉠ — 계약 카드 여러 장을 쌓는 자리.
// 카드가 한 장이면 gap 이 아무 일도 하지 않아 종전(카드 하나)과 같은 모양이다.
const contractCardStack: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16 };
// 카드 머리 — 계약번호 · 형태 · 배정 단계 · 배지(반납 임박 / 마스터 편집 대상).
// 계약이 2건 이상일 때만 붙는다.
const contractCardHead: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  marginBottom: 10,
  paddingBottom: 10,
  borderBottom: '1px solid var(--border-subtle)',
  fontSize: 13,
};
const contractHeadTag: CSSProperties = {
  padding: '2px 8px',
  borderRadius: 'var(--r-pill)',
  fontSize: 11,
  fontWeight: 600,
  background: 'var(--bg-sunken)',
  color: 'var(--fg-muted)',
  border: '1px solid var(--border-default)',
};
// '마스터 편집 대상' — 저장이 닿는 계약. 다른 배지와 눈에 띄게 갈려야 오조작이 줄어든다.
const contractHeadTagStrong: CSSProperties = {
  ...contractHeadTag,
  background: 'var(--status-contracted-tint)',
  color: 'var(--status-contracted)',
  border: '1px solid var(--status-contracted)',
};
// round10 요구1 — CRM 메모 타임라인 항목/수정·삭제 버튼.
const crmNoteItem: CSSProperties = {
  borderLeft: '2px solid var(--border-default)',
  paddingLeft: 10,
};
const crmIconBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--fg-muted)',
  padding: 2,
  flexShrink: 0,
  lineHeight: 0,
};
const sectionHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  marginBottom: 8,
};
const tbl: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  // 머리글 줄 높이는 모델별 계약 내역 표(6px)에 맞춘다(2026-10-22 오너) — 10px 이면 색 띠가 두꺼워 보였다.
  padding: '6px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
// 로봇 정보 표(2026-10-20 오너 — "표 망가지는 거도") — 칸이 좁아지면 '운영 중' · '상태' 가 한 글자씩
// 세로로 쪼개졌다. 칸 안에서 줄을 바꾸지 않고, 넘치면 카드가 옆으로 스크롤한다(card overflow:auto).
const rth: CSSProperties = { ...th, whiteSpace: 'nowrap' };
const rtd: CSSProperties = { ...td, whiteSpace: 'nowrap' };
const rowBtn: CSSProperties = { height: 28, padding: '0 10px', fontSize: 12 };
const historyToggle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 2px',
  border: 'none',
  background: 'transparent',
  color: 'var(--fg-muted)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const kvGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '0 24px',
};
const kvRow: CSSProperties = {
  display: 'flex',
  gap: 12,
  padding: '8px 0',
  borderBottom: '1px solid var(--border-subtle)',
  fontSize: 13,
};
const kvKey: CSSProperties = { width: 90, color: 'var(--fg-muted)' };
const kvVal: CSSProperties = { flex: 1 };
// 비고 줄 — KV 격자 밖 전폭 행. 격자(240px 칸)에 넣으면 긴 메모가 좁은 칸에 구겨진다.
const kvNotesRow: CSSProperties = {
  display: 'flex',
  gap: 12,
  padding: '8px 0',
  borderTop: '1px solid var(--border-subtle)',
  fontSize: 13,
};

// 모델별 계약 내역(구독료/판매가) 표 — 계약 카드 안 소형 테이블.
const modelFeeCaption: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  marginBottom: 6,
};
const modelFeeTable: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
};
const mfThL: CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  borderBottom: '1px solid var(--border-subtle)',
  whiteSpace: 'nowrap',
};
const mfThR: CSSProperties = { ...mfThL, textAlign: 'right' };
const mfTdL: CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--border-subtle, var(--border-default))',
};
const mfTdR: CSSProperties = {
  ...mfTdL,
  textAlign: 'right',
  fontFamily: 'var(--font-mono)',
  whiteSpace: 'nowrap',
};
const mfTdFootL: CSSProperties = {
  padding: '6px 8px',
  fontWeight: 600,
  borderTop: '2px solid var(--border-default)',
};
const mfTdFootR: CSSProperties = {
  ...mfTdFootL,
  textAlign: 'right',
  fontFamily: 'var(--font-mono)',
  whiteSpace: 'nowrap',
};
const crumbStyle: CSSProperties = {
  color: 'var(--fg-muted)',
  textDecoration: 'none',
};
const linkStyle: CSSProperties = {
  color: 'var(--fg-brand)',
  textDecoration: 'none',
  fontSize: 13,
};
const moreLinkStyle: CSSProperties = {
  color: 'var(--fg-muted)',
  textDecoration: 'underline',
  fontSize: 12,
};
const bulkBar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 10px',
  marginBottom: 6,
  borderRadius: 'var(--r-card)',
  background: 'var(--brand-subtle)',
  fontSize: 12,
};

const loadingInline: CSSProperties = {
  textAlign: 'center',
  color: 'var(--fg-muted)',
  padding: 16,
};
const loadingBlock: CSSProperties = {
  padding: 40,
  textAlign: 'center',
  color: 'var(--fg-muted)',
};
const errBlock: CSSProperties = {
  padding: 40,
  textAlign: 'center',
};
